import { useEffect, useState, useRef} from 'react';
import { parseDelimited } from '@/utils/csv-helper';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Users, Wallet, Calendar, ClipboardList, Boxes, Laptop, Wrench, FileSpreadsheet, BarChart3, Plus, Save, X, Trash2, Edit3, Upload, Download, Check, Ban, RotateCcw, Clock, ArrowUpCircle, Settings2, Mail} from 'lucide-react';
import { api } from '@/api/client';
import { useAuth, can } from '@/hooks/useAuth';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useSignedUrl } from '@/components/ui/SignedImage';
import { HrNotificationsTab } from './HrNotificationsTab';

// ─────────────── CSV helpers (export / parse) ───────────────
function csvEscape(v: any): string {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[,"\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}
function downloadCsv(rows: any[], headers: string[], filename: string) {
  const lines = [headers.join(','), ...rows.map(r => headers.map(h => csvEscape(r[h])).join(','))];
  const blob = new Blob(["\uFEFF" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
/**
 * HR import rows, via the shared quote-aware tokeniser (see utils/csv-helper).
 *
 * Empty cells are OMITTED rather than set to '' — the bulk-upsert treats an absent key as "leave
 * this field alone", so writing '' would blank a value the sheet simply did not carry. That
 * behaviour is preserved deliberately.
 */
function parseCsv(text: string): any[] {
  return parseDelimited(text, { mapHeader: (h) => h.trim() }).rows.map((r) => {
    const obj: any = {};
    for (const [k, v] of Object.entries(r)) if (v !== '') obj[k] = v;
    return obj;
  });
}
function ImportExportBar({ rows, headers, filename, onImport, hideImport }: {
  rows: any[]; headers: string[]; filename: string;
  onImport?: (parsed: any[]) => Promise<void> | void;
  hideImport?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex gap-1.5">
      <button onClick={() => downloadCsv(rows ?? [], headers, filename)} disabled={!(rows ?? []).length}
        className="px-2 py-1 text-xs border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 flex items-center gap-1 disabled:opacity-50">
        <Download size={12} /> Export
      </button>
      {!hideImport && onImport && (
        <>
          <button onClick={() => fileRef.current?.click()} disabled={busy}
            className="px-2 py-1 text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50 flex items-center gap-1 disabled:opacity-50">
            <Upload size={12} /> {busy ? 'Importing…' : 'Import'}
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              setBusy(true);
              try {
                const text = await f.text();
                const parsed = parseCsv(text);
                await onImport(parsed);
                toast.success(`Imported ${parsed.length} rows`);
              } catch (err: any) {
                const srv = err?.response?.data?.message;
                toast.error((Array.isArray(srv) ? srv.join(', ') : srv) ?? err?.message ?? 'Import failed');
              }
              finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
            }} />
        </>
      )}
    </div>
  );
}


// ─────────────── Pagination + date-range filter helpers ───────────────
const PAGE_SIZES = [10, 25, 50, 100];

/** Local-time YYYY-MM-DD for a date value (timezone-stable for range compares). */
function ymd(v: any): string | null {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
/** True if a date value falls within [from,to] (inclusive). Blank bounds are open-ended. */
function inDateRange(v: any, from: string, to: string): boolean {
  if (!from && !to) return true;
  const s = ymd(v);
  if (!s) return false;
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
}
/** Same idea for a month value against YYYY-MM bounds (used by the PF/ESI reports). */
function inMonthRange(v: any, from: string, to: string): boolean {
  if (!from && !to) return true;
  const s = ymd(v)?.slice(0, 7);
  if (!s) return false;
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
}

function DateRangeFilter({ from, to, onFrom, onTo, label = 'Date' }:
  { from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void; label?: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-slate-500 whitespace-nowrap">{label}</span>
      <input type="date" value={from} onChange={e => onFrom(e.target.value)} title={`${label} from`} className="px-2 py-1 border rounded text-xs" />
      <span className="text-slate-400 text-xs">–</span>
      <input type="date" value={to} onChange={e => onTo(e.target.value)} title={`${label} to`} className="px-2 py-1 border rounded text-xs" />
      {(from || to) && <button onClick={() => { onFrom(''); onTo(''); }} title="Clear dates" className="text-xs text-slate-400 hover:text-slate-700">✕</button>}
    </div>
  );
}
function MonthRangeFilter({ from, to, onFrom, onTo, label = 'Month' }:
  { from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void; label?: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-slate-500 whitespace-nowrap">{label}</span>
      <input type="month" value={from} onChange={e => onFrom(e.target.value)} title={`${label} from`} className="px-2 py-1 border rounded text-xs" />
      <span className="text-slate-400 text-xs">–</span>
      <input type="month" value={to} onChange={e => onTo(e.target.value)} title={`${label} to`} className="px-2 py-1 border rounded text-xs" />
      {(from || to) && <button onClick={() => { onFrom(''); onTo(''); }} title="Clear months" className="text-xs text-slate-400 hover:text-slate-700">✕</button>}
    </div>
  );
}

/** Client-side pagination over an already-filtered array. Resets to page 1 when `deps` change. */
function usePaged<T>(rows: T[], deps: any[]) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => { setPage(1); }, deps);   // eslint-disable-line react-hooks/exhaustive-deps
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(page, pages);
  const slice = rows.slice((cur - 1) * pageSize, cur * pageSize);
  return { slice, page: cur, setPage, pageSize, setPageSize, total };
}

function Pager({ page, pageSize, total, setPage, setPageSize }:
  { page: number; pageSize: number; total: number; setPage: (p: number) => void; setPageSize: (n: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 mt-2 text-xs text-slate-600">
      <div className="flex items-center gap-1.5">
        <span>Rows per page</span>
        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="px-1.5 py-1 border rounded">
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <span className="tabular-nums">{start}–{end} of {total}</span>
        <div className="flex gap-1">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-slate-50">Prev</button>
          <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages} className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-slate-50">Next</button>
        </div>
      </div>
    </div>
  );
}

type Tab = 'dashboard' | 'employees' | 'salary' | 'attendance' | 'requirements' | 'stock' | 'assets' | 'repairs' | 'pf' | 'esi' | 'notifications';

// `label` is the PERMISSION TAB NAME, not just display text — see `visibleTabs` below. It must match
// the HR tabs list in the API's roles.service.ts exactly, or the tab is invisible to everyone but
// SUPER_ADMIN / ADMIN.
const TABS: Array<{ id: Tab; label: string; icon: any }> = [
  { id: 'dashboard',    label: 'Dashboard',    icon: <BarChart3 size={14} /> },
  { id: 'employees',    label: 'Employees',    icon: <Users size={14} /> },
  { id: 'salary',       label: 'Salary',       icon: <Wallet size={14} /> },
  { id: 'attendance',   label: 'Attendance',   icon: <Calendar size={14} /> },
  { id: 'requirements', label: 'Requirements', icon: <ClipboardList size={14} /> },
  { id: 'stock',        label: 'Stationery Stock', icon: <Boxes size={14} /> },
  { id: 'assets',       label: 'Issued Assets', icon: <Laptop size={14} /> },
  { id: 'repairs',      label: 'Repair & Warranty', icon: <Wrench size={14} /> },
  { id: 'pf',           label: 'PF Report',    icon: <FileSpreadsheet size={14} /> },
  { id: 'esi',          label: 'ESI Report',   icon: <FileSpreadsheet size={14} /> },
  { id: 'notifications', label: 'Notifications', icon: <Mail size={14} /> },
];

export function HrPage() {
  const user = useAuth((s) => s.user);
  // Tab labels match the permission tab names exactly, so gate on t.label.
  const visibleTabs = TABS.filter((t) => can(user, 'HR', t.label, 'view'));
  const [tab, setTab] = useState<Tab>(visibleTabs[0]?.id ?? 'dashboard');
  // If the current tab isn't visible (permissions changed), fall back to the first visible one.
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((t) => t.id === tab)) setTab(visibleTabs[0].id);
  }, [visibleTabs.map((t) => t.id).join(','), tab]);

  if (!visibleTabs.length) {
    return <div className="p-8 text-center text-sm text-slate-500">You don't have access to any HR tabs. Ask an administrator to grant HR permissions.</div>;
  }
  const show = (id: Tab) => tab === id && visibleTabs.some((t) => t.id === id);
  return (
    <div className="p-4 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-xl font-bold text-slate-900">HR Module</h1>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-4">
        {visibleTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${tab===t.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      {show('dashboard') && <DashboardTab />}
      {show('employees') && <EmployeesTab />}
      {show('salary') && <SalaryTab />}
      {show('attendance') && <AttendanceTab />}
      {show('requirements') && <RequirementsTab />}
      {show('stock') && <StockTab />}
      {show('assets') && <AssetsTab />}
      {show('repairs') && <RepairsTab />}
      {show('pf') && <PfTab />}
      {show('esi') && <EsiTab />}
      {show('notifications') && <HrNotificationsTab />}
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: any; hint?: string }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="text-[11px] uppercase text-slate-500 tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5">{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function DashboardTab() {
  const { data } = useQuery({ queryKey: ['hr','dashboard'], queryFn: async () => (await api.get('/hr/dashboard')).data });
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-5 gap-3">
        <StatCard label="Total Employees" value={data.empTotal} />
        <StatCard label="Active" value={data.empActive} />
        <StatCard label="Resigned" value={data.empResigned} />
        <StatCard label="Pending Requirements" value={data.pendingReqs} />
        <StatCard label="Stock alerts" value={data.lowStock} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-slate-200 rounded-lg p-3 bg-white">
          <h3 className="font-semibold text-slate-900 text-sm mb-2">Active by Branch</h3>
          <div className="space-y-1">
            {data.byBranch?.map((b: any) => (
              <div key={b.branch} className="flex justify-between text-sm">
                <span>{b.branch}</span><span className="font-mono">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-slate-200 rounded-lg p-3 bg-white">
          <h3 className="font-semibold text-slate-900 text-sm mb-2">Top Designations</h3>
          <div className="space-y-1">
            {data.byDesignation?.map((b: any) => (
              <div key={b.designation} className="flex justify-between text-sm">
                <span>{b.designation}</span><span className="font-mono">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmployeesTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'active'|'inactive'|'log'>('active');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [editing, setEditing] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(false);
  const { data, isPending } = useQuery({
    queryKey: ['hr','emps', search],
    queryFn: async () => (await api.get('/hr/employees', { params: { search, pageSize: 2000 } })).data as any,
  });
  const save = useMutation({
    mutationFn: async (body: any) => {
      const id = body?.id;
      if (id) return (await api.patch(`/hr/employees/${id}`, body)).data;
      return (await api.post('/hr/employees', body)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','emps'] }); toast.success('Saved'); setShowForm(false); setEditing(null); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });
  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/hr/employees/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','emps'] }); toast.success('Deleted'); },
  });
  const all: any[] = data?.items ?? [];
  const isActive = (e:any) => (e.status ?? 'Active') === 'Active';
  const activeCount = all.filter(isActive).length;
  const inactiveCount = all.length - activeCount;
  const scoped = all.filter(e => view === 'inactive' ? !isActive(e) : isActive(e));
  const filtered = scoped.filter(e => inDateRange(e.doj, from, to));
  const { slice: items, page, setPage, pageSize, setPageSize, total } = usePaged(filtered, [search, view, from, to]);
  const tabCls = (active: boolean) => `px-3 py-1.5 text-sm rounded ${active ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'}`;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button onClick={()=>setView('active')} className={tabCls(view==='active')}>Active ({activeCount})</button>
        <button onClick={()=>setView('inactive')} className={tabCls(view==='inactive')}>Inactive ({inactiveCount})</button>
        <button onClick={()=>setView('log')} className={tabCls(view==='log')}>Activity Log</button>
      </div>
      {view === 'log' ? <EmployeeStatusLogPanel /> : (<>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input placeholder="Search by id/name/email/mobile" value={search} onChange={e => setSearch(e.target.value)} className="px-3 py-1.5 border rounded text-sm w-72" />
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} label="Joining" />
        <div className="ml-auto flex gap-2 items-center">
          <ImportExportBar
            rows={filtered}
            headers={['employeeId','employeeName','fatherHusband','branch','center','designation','position','gender','maritalStatus','bloodGroup','dateOfBirth','doj','dateOfResigning','status','mobile','email','familyNumber','pfNumber','uan','memberId','esiNumber','aadharCard','panCard','dlNo','voterId','bankName','accountName','accountNumber','ifsc','fatherName','fatherAadhar','motherName','motherAadhar','spouseName','spouseAadhar','presentAddress','permanentAddress']}
            filename="hr-employees"
            onImport={async (rows) => { await api.post('/hr/employees/bulk-upsert', { rows }); qc.invalidateQueries({ queryKey: ['hr','emps'] }); qc.invalidateQueries({ queryKey: ['hr','dashboard'] }); }}
          />
          <button onClick={() => { setEditing({}); setShowForm(true); }} className="bg-primary text-white px-3 py-1.5 text-sm rounded flex items-center gap-1"><Plus size={14} /> Add Employee</button>
        </div>
      </div>
      <div className="text-xs text-slate-500 mb-2">{total} {view==='inactive'?'inactive':'active'} employee{total === 1 ? '' : 's'}{(search || from || to) ? ' (filtered)' : ''} · {all.length} loaded</div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Emp ID</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-left px-3 py-2">Designation</th>
              <th className="text-left px-3 py-2">DOJ</th>
              <th className="text-left px-3 py-2">Mobile</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Net Pay</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs">{e.employeeId}</td>
                <td className="px-3 py-2 font-medium">{e.employeeName}</td>
                <td className="px-3 py-2">{e.branch ?? '—'}</td>
                <td className="px-3 py-2">{e.designation ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{e.doj ? new Date(e.doj).toLocaleDateString() : '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{e.mobile}</td>
                <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${e.status==='Active'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-600'}`}>{e.status}</span></td>
                <td className="px-3 py-2 font-mono text-xs">{e.salary?.netPayable ? `₹${Number(e.salary.netPayable).toLocaleString('en-IN')}` : '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => { setEditing(e); setShowForm(true); }} className="text-xs text-primary mr-2"><Edit3 size={12} className="inline" /></button>
                  <button onClick={() => { if (confirm('Delete '+e.employeeName+'?')) del.mutate(e.id); }} className="text-xs text-red-600"><Trash2 size={12} className="inline" /></button>
                </td>
              </tr>
            ))}
            {items.length===0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400">{isPending ? 'Loading employees…' : 'No employees'}</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
      </>)}
      {showForm && <EmployeeForm initial={editing} onSave={(d: any) => save.mutate(d)} onClose={() => { setShowForm(false); setEditing(null); }} />}
    </div>
  );
}

// Audit trail of Active/Inactive status changes across all employees.
function EmployeeStatusLogPanel() {
  const { data: logs } = useQuery({ queryKey: ['hr','emp-status-logs'], queryFn: async () => (await api.get('/hr/employee-status-logs')).data as any[] });
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Change</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Remarks</th></tr></thead>
        <tbody>
          {(logs ?? []).map((l:any) => <tr key={l.id} className="border-t">
            <td className="px-3 py-1.5 text-xs whitespace-nowrap">{new Date(l.createdAt).toLocaleString()}</td>
            <td className="px-3 py-1.5"><div className="font-medium text-xs">{l.employee?.employeeName ?? '—'}</div><div className="font-mono text-[10px] text-slate-500">{l.employee?.employeeId}</div></td>
            <td className="px-3 py-1.5 text-xs"><span className="text-slate-500">{l.fromStatus ?? '—'}</span> → <span className={`font-medium ${l.toStatus==='Active'?'text-emerald-700':'text-rose-700'}`}>{l.toStatus}</span></td>
            <td className="px-3 py-1.5 text-xs">{l.actorName ?? '—'}</td>
            <td className="px-3 py-1.5 text-xs text-slate-500">{l.remarks ?? '—'}</td>
          </tr>)}
          {(logs ?? []).length===0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">No status changes recorded yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function DocUpload({ label, value, onChange, required }: { label: string; value?: string; onChange: (url: string|null)=>void; required?: boolean }) {
  const [busy, setBusy] = useState(false);
  const signed = useSignedUrl(value);   // resolve an R2 ref to a viewable URL (data:/http pass through)
  const isPdf = typeof value === 'string' && (value.startsWith('data:application/pdf') || value.toLowerCase().endsWith('.pdf'));
  const handle = async (e: any) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/hr/uploads/doc', fd, { timeout: 120000 });  // axios sets multipart boundary from FormData
      onChange(res.data.url);
      toast.success(`${label} uploaded`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? `${label} upload failed`);
    } finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className={`border rounded p-2 ${required && !value ? 'border-rose-300 bg-rose-50' : 'border-slate-200'}`}>
      <label className="block text-[11px] text-slate-600 mb-1">{label}{required && <span className="text-rose-600"> *</span>}</label>
      {value ? (
        <div className="flex items-center gap-2">
          {isPdf ? <FileSpreadsheet size={28} className="text-slate-400" /> : <img src={signed} alt={label} className="h-12 w-12 object-cover rounded border" />}
          <a href={signed || '#'} target="_blank" rel="noreferrer" className="text-xs text-primary underline">View</a>
          <button type="button" onClick={()=>onChange(null)} className="text-xs text-rose-600">Remove</button>
        </div>
      ) : (
        <input type="file" accept="image/*,application/pdf" disabled={busy} onChange={handle} className="text-xs w-full file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-primary file:text-white file:cursor-pointer cursor-pointer" />
      )}
      {busy && <div className="text-[10px] text-slate-500 mt-1">Uploading…</div>}
    </div>
  );
}

const EMP_TABS = ['Personal','Employment','Education','Family','KYC & Banking'] as const;
function EmployeeForm({ initial, onSave, onClose }: { initial: any; onSave: (d: any) => void; onClose: () => void }) {
  const [d, setD] = useState<any>({ status: 'Active', pfType: 'Basic', ...initial });
  const [tab, setTab] = useState<typeof EMP_TABS[number]>('Personal');
  // The employees LIST omits the heavy base64 document fields (photo/PAN/Aadhaar/DL) for speed,
  // so when editing an existing employee we fetch the full record and merge in those docs —
  // otherwise the "Aadhaar required" guard would block save and the doc previews would be blank.
  // Merge order keeps any edits the user already made (p) on top of the fetched record.
  const { data: full } = useQuery({
    queryKey: ['hr','emp-full', initial?.id],
    queryFn: async () => (await api.get(`/hr/employees/${initial.id}`)).data,
    enabled: !!initial?.id,
  });
  useEffect(() => { if (full) setD((p: any) => ({ ...full, ...p })); }, [full]);
  // Active employees for the Team Manager picker (used to route this employee's requests for approval).
  const { data: mgrList } = useQuery({
    queryKey: ['hr','emp-managers'],
    queryFn: async () => (await api.get('/hr/employees', { params: { status: 'Active', pageSize: 5000 } })).data?.items as any[],
  });
  const managerOptions = (mgrList ?? [])
    .filter((e: any) => e.id !== d.id)   // can't be your own manager
    .map((e: any) => ({ value: e.id, label: `${e.employeeName}${e.employeeId ? ` (${e.employeeId})` : ''}` }));
  const set = (name: string, v: any) => setD((p: any) => ({ ...p, [name]: v }));
  const F = (label: string, name: string, type: string = 'text', extra: any = {}) => {
    // <input type="date"> only renders a value in strict yyyy-MM-dd form. The API returns
    // dates as full ISO strings (e.g. "2020-01-15T00:00:00.000Z"), so slice to the date part
    // when editing — otherwise DOB / DOJ / etc. show up blank on the edit form.
    const raw = d[name];
    const val = type === 'date' && raw ? String(raw).slice(0, 10) : (raw ?? '');
    return (
      <div>
        <label className="block text-[11px] text-slate-500 mb-0.5">{label}</label>
        <input type={type} {...extra} value={val} onChange={(e) => set(name, e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" />
      </div>
    );
  };
  const S = (label: string, name: string, options: string[], dflt = '') => (
    <div>
      <label className="block text-[11px] text-slate-500 mb-0.5">{label}</label>
      <select value={d[name] ?? dflt} onChange={(e) => set(name, e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-sm">
        {!dflt && <option value="">—</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  const trySave = () => {
    if (!d.employeeId || !d.employeeName) { toast.error('Employee ID and Employee Name are required'); setTab('Personal'); return; }
    if (!d.aadharFileUrl) { toast.error('Aadhaar document is required — please upload it under KYC & Banking'); setTab('KYC & Banking'); return; }
    onSave(d);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-[1100px] p-5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-bold">{d.id ? 'Edit' : 'Add'} Employee</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex gap-1 border-b border-slate-200 mb-4">
          {EMP_TABS.map(t => (
            <button key={t} onClick={()=>setTab(t)} className={`px-3 py-1.5 text-sm rounded-t ${tab===t?'bg-primary text-white':'text-slate-600 hover:bg-slate-100'}`}>
              {t}{t==='KYC & Banking' && !d.aadharFileUrl ? <span className="text-rose-400"> •</span> : ''}
            </button>
          ))}
        </div>

        {tab==='Personal' && <>
          <div className="grid grid-cols-4 gap-3">
            {F('Employee ID *','employeeId')}
            {F('Employee Name *','employeeName')}
            {F('Father / Husband','fatherHusband')}
            {F('Blood Group','bloodGroup')}
            {F('DOB','dateOfBirth','date')}
            {S('Gender','gender',['MALE','FEMALE','OTHER'])}
            {S('Marital Status','maritalStatus',['SINGLE','MARRIED','DIVORCED'])}
            {F('Email','email','email')}
            {F('Mobile','mobile','tel',{maxLength:10})}
            {F('Family Number','familyNumber','tel',{maxLength:10})}
            {F('Emergency Contact Name','emergencyName')}
            {F('Emergency Relation','emergencyRelation')}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><label className="block text-[11px] text-slate-500 mb-0.5">Present Address</label>
              <textarea value={d.presentAddress ?? ''} onChange={e=>set('presentAddress',e.target.value)} className="w-full px-2 py-1 border rounded text-sm" rows={2} /></div>
            <div><label className="block text-[11px] text-slate-500 mb-0.5">Permanent Address</label>
              <textarea value={d.permanentAddress ?? ''} onChange={e=>set('permanentAddress',e.target.value)} className="w-full px-2 py-1 border rounded text-sm" rows={2} /></div>
          </div>
        </>}

        {tab==='Employment' && <div className="grid grid-cols-4 gap-3">
          {F('Branch','branch')}
          {F('Center','center')}
          {F('Designation','designation')}
          {F('Position','position')}
          {F('DOJ','doj','date')}
          {F('Date of Resigning','dateOfResigning','date')}
          {S('Status','status',['Active','Resigned','Terminated','Suspended'],'Active')}
          {F('PF Number','pfNumber')}
          {F('UAN','uan')}
          {F('Member ID','memberId')}
          {F('ESI Number','esiNumber')}
          <div><label className="block text-[11px] text-slate-500 mb-0.5">PF Type</label>
            <select value={d.pfType ?? 'Basic'} onChange={e=>set('pfType',e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-sm"><option value="Basic">Basic (both sides capped at ₹15k)</option><option value="Advance">Advance (employee 12% on full basic)</option></select></div>
          <div className="col-span-2">
            <label className="block text-[11px] text-slate-500 mb-0.5">Team Manager <span className="text-slate-400">(approves this employee&apos;s Asset/Stationery requests)</span></label>
            <SearchableSelect value={d.reportingManagerId ?? ''} onChange={(v)=>set('reportingManagerId', v === '' ? null : v)} options={managerOptions} placeholder="— select Team Manager —" />
          </div>
        </div>}

        {tab==='Education' && <>
          <div className="grid grid-cols-3 gap-3">
            {F('Highest Qualification','qualification')}
            {F('Course','course')}
            {F('Specialization','specialization')}
            {F('Institution / University','institution')}
            {F('Year of Passing','yearOfPassing')}
          </div>
          <div className="mt-3"><label className="block text-[11px] text-slate-500 mb-0.5">Additional Qualifications / Notes</label>
            <textarea value={d.educationDetails ?? ''} onChange={e=>set('educationDetails',e.target.value)} className="w-full px-2 py-1 border rounded text-sm" rows={3} /></div>
        </>}

        {tab==='Family' && <div className="grid grid-cols-3 gap-3">
          {F('Father Name','fatherName')}
          {F('Father Aadhar','fatherAadhar')}
          {F('Father DOB','fatherDob','date')}
          {F('Mother Name','motherName')}
          {F('Mother Aadhar','motherAadhar')}
          {F('Mother DOB','motherDob','date')}
          {F('Spouse Name','spouseName')}
          {F('Spouse Aadhar','spouseAadhar')}
        </div>}

        {tab==='KYC & Banking' && <>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {F('Aadhar Card No','aadharCard')}
            {F('PAN Card No','panCard')}
            {F('DL No','dlNo')}
            {F('Voter ID','voterId')}
            {F('Bank Name','bankName')}
            {F('Account Name','accountName')}
            {F('Account Number','accountNumber')}
            {F('IFSC','ifsc')}
          </div>
          <div className="text-[11px] text-slate-500 mb-1 font-medium">Documents (JPG / PNG / PDF, under 5 MB) — Aadhaar is required</div>
          <div className="grid grid-cols-3 gap-3">
            <DocUpload label="Aadhaar Document" required value={d.aadharFileUrl} onChange={u=>set('aadharFileUrl',u)} />
            <DocUpload label="PAN Document" value={d.panFileUrl} onChange={u=>set('panFileUrl',u)} />
            <DocUpload label="DL Document" value={d.dlFileUrl} onChange={u=>set('dlFileUrl',u)} />
          </div>
        </>}

        <div className="flex justify-between items-center gap-2 mt-5 pt-3 border-t border-slate-100">
          <div className="text-[11px] text-slate-400">{!d.aadharFileUrl && <span className="text-rose-500">Aadhaar document required before saving</span>}</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
            <button onClick={trySave} className="bg-primary text-white px-4 py-1.5 text-sm rounded flex items-center gap-1"><Save size={14} /> Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SalaryTab() {
  const qc = useQueryClient();
  const { data: emps } = useQuery({ queryKey: ['hr','emp-options'], queryFn: async () => (await api.get('/hr/employees', { params: { pageSize: 500 } })).data });
  const [empId, setEmpId] = useState<number | null>(null);
  const [form, setForm] = useState<any>({ basic: 0, hra: 0, others: 0, transportAllowance: 0, incentives: 0, telephoneAllowance: 0, tds: 0 });
  const items: any[] = emps?.items ?? [];
  useEffect(() => {
    if (empId) {
      const emp = items.find((e: any) => e.id === empId);
      if (emp?.salary) setForm({ ...form, ...emp.salary });
    }
  }, [empId]);
  const save = useMutation({
    mutationFn: async () => (await api.post(`/hr/employees/${empId}/salary`, form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','emp-options'] }); toast.success('Saved'); },
  });
  const selectedEmp = items.find((e: any) => e.id === empId);
  const advance = String(selectedEmp?.pfType ?? 'Basic').toLowerCase() === 'advance';
  const previewCalc = () => {
    const b = Number(form.basic ?? 0), h = Number(form.hra ?? 0), o = Number(form.others ?? 0), t = Number(form.transportAllowance ?? 0), i = Number(form.incentives ?? 0), tel = Number(form.telephoneAllowance ?? 0);
    const gross = b + h + o + t + i + tel;
    const employerPfBasis = Math.min(b, 15000);            // employer always capped at 15k
    const employeePfBasis = advance ? b : Math.min(b, 15000); // Advance = employee 12% on full basic
    return { gross, employerPf: +(employerPfBasis * 0.12).toFixed(2), employeePf: +(employeePfBasis * 0.12).toFixed(2), employerEsi: gross <= 21000 ? +(gross * 0.0325).toFixed(2) : 0, employeeEsi: gross <= 21000 ? +(gross * 0.0075).toFixed(2) : 0 };
  };
  const calc = previewCalc();
  const net = (calc.gross - calc.employeePf - calc.employeeEsi - Number(form.tds ?? 0));
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-end">
        <div>
        <label className="block text-xs text-slate-500 mb-1">Select Employee</label>
        <select value={empId ?? ''} onChange={e => setEmpId(Number(e.target.value) || null)} className="px-3 py-1.5 border rounded text-sm w-96">
          <option value="">— pick —</option>
          {items.map((e: any) => <option key={e.id} value={e.id}>{e.employeeId} — {e.employeeName}</option>)}
        </select>
        </div>
        <ImportExportBar rows={items.map((e:any) => ({ employeeId: e.employeeId, employeeName: e.employeeName, basic: e.salary?.basic, hra: e.salary?.hra, others: e.salary?.others, transportAllowance: e.salary?.transportAllowance, incentives: e.salary?.incentives, telephoneAllowance: e.salary?.telephoneAllowance, grossSalary: e.salary?.grossSalary, employerPf: e.salary?.employerPf, employerEsi: e.salary?.employerEsi, ctc: e.salary?.ctc, employeePf: e.salary?.employeePf, employeeEsi: e.salary?.employeeEsi, tds: e.salary?.tds, netPayable: e.salary?.netPayable }))}
          headers={['employeeId','employeeName','basic','hra','others','transportAllowance','incentives','telephoneAllowance','grossSalary','employerPf','employerEsi','ctc','employeePf','employeeEsi','tds','netPayable']}
          filename="hr-salary"
          onImport={async (rows) => {
            const empMap = new Map<string, any>(items.map((e: any) => [e.employeeId, e]));
            for (const r of rows) { const emp = empMap.get(String(r.employeeId).trim()); if (!emp) continue; await api.post(`/hr/employees/${emp.id}/salary`, r); }
            qc.invalidateQueries({ queryKey: ['hr','emp-options'] });
          }}
        />
      </div>
      {empId && (
        <div className="border border-slate-200 rounded-lg p-4 bg-white max-w-2xl">
          <div className="grid grid-cols-2 gap-3 mb-3">
            {(['basic','hra','others','transportAllowance','incentives','telephoneAllowance','tds'] as const).map(k => (
              <div key={k}>
                <label className="block text-[11px] text-slate-500 mb-0.5">{k.replace(/([A-Z])/g,' $1').replace(/^./, c => c.toUpperCase())}</label>
                <input type="number" value={form[k] ?? 0} onChange={e => setForm({ ...form, [k]: e.target.value })} className="w-full px-2 py-1 border rounded text-sm font-mono" />
              </div>
            ))}
          </div>
          <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
            <div className="flex justify-between"><span>Gross Salary</span><b>₹{calc.gross.toLocaleString('en-IN')}</b></div>
            <div className="flex justify-between"><span>Employer PF (12% of basic, capped ₹15k)</span><span>₹{calc.employerPf}</span></div>
            <div className="flex justify-between"><span>Employer ESI (3.25%, if gross ≤21000)</span><span>₹{calc.employerEsi}</span></div>
            <div className="flex justify-between"><span>Employee PF (12% of {advance ? 'full basic — Advance' : 'basic capped ₹15k — Basic'})</span><span>₹{calc.employeePf}</span></div>
            <div className="flex justify-between"><span>Employee ESI (0.75%, if gross ≤21000)</span><span>₹{calc.employeeEsi}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1 font-bold"><span>Net Payable</span><span className="font-mono text-emerald-700">₹{net.toFixed(2)}</span></div>
            <div className="flex justify-between text-xs text-slate-500"><span>CTC</span><span>₹{(calc.gross + calc.employerPf + calc.employerEsi).toFixed(2)}</span></div>
          </div>
          <button onClick={() => save.mutate()} className="mt-3 bg-primary text-white px-4 py-1.5 text-sm rounded flex items-center gap-1"><Save size={14} /> Save Salary</button>
        </div>
      )}
    </div>
  );
}

/** Time-of-day in IST, e.g. "09:42 am" — for today's check-in / check-out stamps. */
function fmtTimeIST(dt: any): string {
  if (!dt) return '—';
  try { return new Date(dt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(dt); }
}

/**
 * Today's check-in / check-out summary, shown above the monthly grid.
 * Counts are DISTINCT employees from the selfie/GPS punch log (that's where real in/out times live —
 * the grid itself only holds a per-day P/A/H status).
 */
function TodayAttendanceSummary() {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'att', 'today'],
    queryFn: async () => (await api.get('/hr/attendance/today')).data as any,
    refetchInterval: 60000,
  });
  if (isLoading || !data) return <div className="mb-3 text-sm text-slate-500">Loading today's attendance…</div>;
  const tiles = [
    { label: 'Checked In',     value: data.checkedIn,     tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    { label: 'Checked Out',    value: data.checkedOut,    tone: 'text-sky-700 bg-sky-50 border-sky-200' },
    { label: 'Still In',       value: data.stillIn,       tone: 'text-amber-700 bg-amber-50 border-amber-200' },
    { label: 'Not Checked In', value: data.notCheckedIn,  tone: 'text-rose-700 bg-rose-50 border-rose-200' },
    { label: 'Active Staff',   value: data.activeEmployees, tone: 'text-slate-700 bg-slate-50 border-slate-200' },
  ];
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Today · {data.date}</span>
        {data.totalHours > 0 && <span className="text-xs text-slate-500">· {data.totalHours} hrs logged</span>}
        <button onClick={() => setOpen((v) => !v)} className="ml-auto text-xs text-primary hover:underline">
          {open ? 'Hide' : 'Show'} who checked in ({data.rows?.length ?? 0})
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {tiles.map((t) => (
          <div key={t.label} className={`border rounded-lg px-3 py-2 ${t.tone}`}>
            <div className="text-xl font-bold leading-tight">{t.value}</div>
            <div className="text-[11px] font-medium opacity-80">{t.label}</div>
          </div>
        ))}
      </div>
      {open && (
        <div className="mt-2 overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Emp ID</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Check In</th>
                <th className="text-left px-3 py-2">Check Out</th>
                <th className="text-left px-3 py-2">Location</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data.rows ?? []).map((r: any) => (
                <tr key={r.employeeId} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono text-[10px]">{r.employeeId}</td>
                  <td className="px-3 py-1.5">{r.employeeName}</td>
                  <td className="px-3 py-1.5 text-emerald-700">{fmtTimeIST(r.inAt)}</td>
                  <td className="px-3 py-1.5 text-sky-700">{fmtTimeIST(r.outAt)}</td>
                  <td className="px-3 py-1.5 text-slate-600">{r.city ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.status === 'IN' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                      {r.status === 'IN' ? 'Still in' : 'Checked out'}
                    </span>
                  </td>
                </tr>
              ))}
              {(data.rows ?? []).length === 0 && (
                <tr><td colSpan={6} className="px-3 py-5 text-center text-slate-400">Nobody has checked in yet today.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MonthlyAttendanceGrid() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['hr','att', year, month],
    queryFn: async () => (await api.get('/hr/attendance', { params: { year, month } })).data,
  });
  const mark = useMutation({
    mutationFn: async (rows: any[]) => (await api.post('/hr/attendance/mark', { rows })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','att'] }); toast.success('Marked'); },
  });
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const days = data.days;
  return (
    <div>
      <TodayAttendanceSummary />
      <div className="flex items-center gap-2 mb-3">
        <select value={month} onChange={e=>setMonth(Number(e.target.value))} className="px-2 py-1 border rounded text-sm">
          {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        <select value={year} onChange={e=>setYear(Number(e.target.value))} className="px-2 py-1 border rounded text-sm">
          {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-slate-500">Click a cell to cycle: P → A → H → SUN → blank</span>
        <div className="ml-auto">
          <ImportExportBar
            rows={data.employees.flatMap((e: any) => e.days.map((d: any) => ({ employeeId: e.employeeId, employeeName: e.employeeName, attDate: d.attDate?.slice(0,10), status: d.status, inTime: d.inTime, outTime: d.outTime, remarks: d.remarks })))}
            headers={['employeeId','employeeName','attDate','status','inTime','outTime','remarks']}
            filename={`hr-attendance-${year}-${String(month).padStart(2,'0')}`}
            onImport={async (rows) => {
              const empMap = new Map<string, number>(data.employees.map((e: any) => [e.employeeId, e.id]));
              const payload = rows.map((r: any) => ({ employeeId: empMap.get(String(r.employeeId).trim()), attDate: r.attDate, status: r.status, inTime: r.inTime, outTime: r.outTime, remarks: r.remarks })).filter((r: any) => r.employeeId && r.attDate && r.status);
              if (payload.length) await api.post('/hr/attendance/mark', { rows: payload });
              qc.invalidateQueries({ queryKey: ['hr','att'] });
            }}
          />
        </div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-1.5 text-left sticky left-0 bg-slate-50 z-10">Emp ID</th>
              <th className="px-2 py-1.5 text-left sticky left-16 bg-slate-50 z-10">Name</th>
              {Array.from({length: days}, (_,i) => i+1).map(d => <th key={d} className="px-1 py-1.5 w-7 text-center">{d}</th>)}
              <th className="px-2 py-1.5">P</th><th className="px-2 py-1.5">A</th>
            </tr>
          </thead>
          <tbody>
            {data.employees.map((e: any) => {
              const map: Record<number, string> = {};
              for (const d of e.days) map[new Date(d.attDate).getDate()] = d.status;
              const pCount = Object.values(map).filter(s => s === 'P' || s === 'HD').length;
              const aCount = Object.values(map).filter(s => s === 'A').length;
              const cycle = (s: string | undefined) => s === 'P' ? 'A' : s === 'A' ? 'H' : s === 'H' ? 'SUN' : s === 'SUN' ? '' : 'P';
              return (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-2 py-1 font-mono text-[10px] sticky left-0 bg-white">{e.employeeId}</td>
                  <td className="px-2 py-1 sticky left-16 bg-white">{e.employeeName}</td>
                  {Array.from({length: days}, (_,i) => i+1).map(d => {
                    const s = map[d];
                    const color = s === 'P' || s === 'HD' ? 'bg-emerald-100 text-emerald-700' : s === 'A' ? 'bg-rose-100 text-rose-700' : s === 'H' ? 'bg-amber-100 text-amber-700' : s === 'SUN' ? 'bg-slate-100 text-slate-500' : '';
                    return (
                      <td key={d} className={`px-0.5 py-0.5 text-center font-mono cursor-pointer hover:ring-1 ring-primary ${color}`}
                        onClick={() => mark.mutate([{ employeeId: e.id, attDate: `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`, status: cycle(s) }])}>
                        {s ?? ''}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1 text-center font-bold text-emerald-700">{pCount}</td>
                  <td className="px-2 py-1 text-center font-bold text-rose-700">{aCount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmtIST(dt: any): string {
  if (!dt) return '';
  try { return new Date(dt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(dt); }
}

function AttendanceTab() {
  const [sub, setSub] = useState<'grid' | 'self'>('grid');
  const tabCls = (active: boolean) => `px-3 py-1.5 text-sm rounded ${active ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'}`;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setSub('grid')} className={tabCls(sub === 'grid')}>Monthly Grid</button>
        <button onClick={() => setSub('self')} className={tabCls(sub === 'self')}>Self-Attendance (Selfie / GPS)</button>
      </div>
      {sub === 'grid' ? <MonthlyAttendanceGrid /> : <SelfAttendancePanel />}
    </div>
  );
}

function LocationDetailModal({ city, rows, onClose }: { city: string; rows: any[]; onClose: () => void }) {
  const exportRows = rows.map(r => ({
    employeeId: r.employeeCode ?? '', employeeName: r.employeeName ?? '', dateTime: fmtIST(r.markedAt),
    type: r.kind ?? 'IN', area: r.area ?? '', city: r.city ?? '', district: r.district ?? '', state: r.state ?? '',
    pincode: r.pincode ?? '', latitude: r.latitude ?? '', longitude: r.longitude ?? '', hoursWorked: r.hoursWorked ?? '',
  }));
  const headers = ['employeeId', 'employeeName', 'dateTime', 'type', 'area', 'city', 'district', 'state', 'pincode', 'latitude', 'longitude', 'hoursWorked'];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 pt-12" onClick={onClose}>
      <div className="bg-white rounded-lg w-[900px] max-w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
          <div className="font-semibold text-slate-800">Attendance at <span className="text-emerald-700">{city}</span> · {rows.length} mark{rows.length === 1 ? '' : 's'}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => downloadCsv(exportRows, headers, `attendance-${city}`)} disabled={!rows.length}
              className="px-2 py-1 text-xs border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 flex items-center gap-1 disabled:opacity-50"><Download size={12} /> Export</button>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>
        <div className="overflow-auto p-3">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr>
              <th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Date &amp; Time</th>
              <th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Area</th>
              <th className="text-left px-3 py-2">Pincode</th><th className="text-left px-3 py-2">GPS</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2"><div className="font-medium text-slate-900">{r.employeeName}</div><div className="text-[11px] text-slate-500 font-mono">{r.employeeCode}</div></td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">{fmtIST(r.markedAt)}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${r.kind === 'OUT' ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-700'}`}>{r.kind ?? 'IN'}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-600">{[r.area, r.district].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-xs font-mono">{r.pincode || '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.latitude != null && r.longitude != null ? <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline font-mono">map</a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// IST date (YYYY-MM-DD) offset by `daysBack` days from today. Shifting the clock by +5:30 makes the
// UTC calendar fields read as IST wall time, so ISO-slice gives the correct Indian date regardless of TZ.
function istYmd(daysBack = 0): string {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  d.setUTCDate(d.getUTCDate() - daysBack);
  return d.toISOString().slice(0, 10);
}
// Start of the current IST week (Monday).
function istWeekStart(): string {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  const daysSinceMon = (d.getUTCDay() + 6) % 7; // 0=Sun..6=Sat -> days since Monday
  return istYmd(daysSinceMon);
}
// First day of the current IST month.
function istMonthStart(): string {
  return istYmd(0).slice(0, 8) + '01';
}

function SelfAttendancePanel() {
  // Default the listing to TODAY (IST) so it opens on today's attendance; the user can widen/clear the range.
  const [from, setFrom] = useState(istYmd(0));
  const [to, setTo] = useState(istYmd(0));
  const ATT_PRESETS: { l: string; from: () => string; to: () => string }[] = [
    { l: 'Today', from: () => istYmd(0), to: () => istYmd(0) },
    { l: 'Yesterday', from: () => istYmd(1), to: () => istYmd(1) },
    { l: 'This Week', from: () => istWeekStart(), to: () => istYmd(0) },
    { l: 'This Month', from: () => istMonthStart(), to: () => istYmd(0) },
    { l: 'Last 7 days', from: () => istYmd(6), to: () => istYmd(0) },
    { l: 'Last 30 days', from: () => istYmd(29), to: () => istYmd(0) },
  ];
  const activePreset = ATT_PRESETS.find((p) => p.from() === from && p.to() === to)?.l ?? null;
  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'self-att', from, to],
    queryFn: async () => (await api.get('/self-attendance', { params: { pageSize: 5000, ...(from && { from }), ...(to && { to }) } })).data as any,
  });
  const rows: any[] = data?.rows ?? [];
  const [preview, setPreview] = useState<string | null>(null);
  const [locDetail, setLocDetail] = useState<string | null>(null);

  // Per-employee-per-day In/Out pivot: pairs each day's first IN with its last OUT so the export shows
  // explicit In Time / Out Time columns (the scan log alone only has one timestamp + a kind per row).
  const istDay = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const istTime = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false, hour: '2-digit', minute: '2-digit' });
  const inoutMap = new Map<string, any>();
  for (const r of rows) {
    if (!r.markedAt) continue;
    const key = `${r.employeeCode || r.employeeName}|${istDay(r.markedAt)}`;
    const cur = inoutMap.get(key) ?? { employeeId: r.employeeCode ?? '', employeeName: r.employeeName ?? '', date: istDay(r.markedAt), inTime: '', outTime: '', hoursWorked: '', city: r.city || r.district || r.area || '', state: r.state ?? '' };
    const t = istTime(r.markedAt);
    if ((r.kind ?? 'IN') === 'OUT') { if (!cur.outTime || t > cur.outTime) cur.outTime = t; }
    else { if (!cur.inTime || t < cur.inTime) cur.inTime = t; }
    if (r.hoursWorked != null && r.hoursWorked !== '') cur.hoursWorked = r.hoursWorked;
    inoutMap.set(key, cur);
  }
  const inoutRows = Array.from(inoutMap.values()).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : String(a.employeeName).localeCompare(String(b.employeeName))));
  const inoutHeaders = ['employeeId', 'employeeName', 'date', 'inTime', 'outTime', 'hoursWorked', 'city', 'state'];

  const exportRows = rows.map(r => ({
    employeeId: r.employeeCode ?? '',
    employeeName: r.employeeName ?? '',
    dateTime: fmtIST(r.markedAt),
    type: r.kind ?? 'IN',
    area: r.area ?? '',
    city: r.city ?? '',
    district: r.district ?? '',
    state: r.state ?? '',
    pincode: r.pincode ?? '',
    latitude: r.latitude ?? '',
    longitude: r.longitude ?? '',
    accuracyMeters: r.accuracy ?? '',
    ipAddress: r.ipAddress ?? '',
    deviceType: r.deviceType ?? '',
    os: r.os ?? '',
    hoursWorked: r.hoursWorked ?? '',
    selfieLink: r.selfieLink ?? '',
  }));
  const headers = ['employeeId', 'employeeName', 'dateTime', 'type', 'area', 'city', 'district', 'state', 'pincode', 'latitude', 'longitude', 'accuracyMeters', 'ipAddress', 'deviceType', 'os', 'hoursWorked', 'selfieLink'];
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} label="Marked" />
        <div className="flex flex-wrap gap-1">
          {ATT_PRESETS.map((p) => (
            <button key={p.l} onClick={() => { setFrom(p.from()); setTo(p.to()); }}
              className={`text-xs px-2 py-1.5 border rounded ${activePreset === p.l ? 'bg-primary text-white border-primary' : 'text-slate-600 hover:bg-slate-50'}`}>{p.l}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">{data?.total ?? 0} record{(data?.total ?? 0) === 1 ? '' : 's'}</span>
          <button onClick={() => downloadCsv(inoutRows, inoutHeaders, 'attendance-in-out')} disabled={!inoutRows.length}
            title="Export one row per employee per day with In Time, Out Time and Hours"
            className="px-2 py-1 text-xs border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50 flex items-center gap-1 disabled:opacity-50">
            <Download size={12} /> Export In/Out
          </button>
          <ImportExportBar rows={exportRows} headers={headers} filename="self-attendance" hideImport />
        </div>
      </div>
      {isLoading ? <div className="text-sm text-slate-500">Loading…</div> : (
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Selfie</th>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Date &amp; Time</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-left px-3 py-2">Pincode</th>
              <th className="text-left px-3 py-2">GPS</th>
              <th className="text-left px-3 py-2">IP</th>
              <th className="text-left px-3 py-2">Device</th>
              <th className="text-left px-3 py-2">Hours</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400">No self-attendance records{(from || to) ? ' for this range' : ''}.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  {r.selfieLink
                    ? <button type="button" onClick={() => setPreview(r.selfieLink)} title="View selfie"><img src={r.selfieLink} alt="selfie" className="w-9 h-9 rounded object-cover hover:ring-2 hover:ring-primary cursor-zoom-in" /></button>
                    : <span className="text-slate-300 text-xs">—</span>}
                </td>
                <td className="px-3 py-2"><div className="font-medium text-slate-900">{r.employeeName}</div><div className="text-[11px] text-slate-500 font-mono">{r.employeeCode}</div></td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-700">{fmtIST(r.markedAt)}</td>
                <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${r.kind === 'OUT' ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-700'}`}>{r.kind ?? 'IN'}</span></td>
                <td className="px-3 py-2 text-xs text-slate-600">{[r.area, r.city, r.district, r.state].filter(Boolean).filter((v: string, i: number, a: string[]) => a.indexOf(v) === i).join(', ') || '—'}</td>
                <td className="px-3 py-2 text-xs font-mono text-slate-700">{r.pincode || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {r.latitude != null && r.longitude != null
                    ? <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline font-mono">{Number(r.latitude).toFixed(4)}, {Number(r.longitude).toFixed(4)}</a>
                    : '—'}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.ipAddress ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.deviceType ?? '—'}{r.os ? ` · ${r.os}` : ''}</td>
                <td className="px-3 py-2 text-xs font-semibold text-emerald-700">{r.hoursWorked != null ? `${r.hoursWorked}h` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {!isLoading && rows.length > 0 && (() => {
        // Group marks by city to show where the team is punching in from.
        const byCity = new Map<string, { count: number; employees: Set<string>; state?: string }>();
        for (const r of rows) {
          const city = r.city || r.district || r.area || 'Unknown';
          const cur = byCity.get(city) ?? { count: 0, employees: new Set<string>(), state: r.state };
          cur.count++;
          const who = r.employeeCode || r.employeeName;
          if (who) cur.employees.add(who);
          if (!cur.state && r.state) cur.state = r.state;
          byCity.set(city, cur);
        }
        const summary = Array.from(byCity.entries()).map(([city, v]) => ({ city, ...v })).sort((a, b) => b.count - a.count);
        const withGps = rows.filter(r => r.latitude != null && r.longitude != null).length;
        return (
          <div className="mt-4 border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="text-sm font-semibold text-slate-800 mb-1">Location Summary</div>
            <div className="text-xs text-slate-500 mb-2">
              {rows.length} mark{rows.length === 1 ? '' : 's'} across {summary.length} location{summary.length === 1 ? '' : 's'} · {withGps} with GPS
            </div>
            <div className="flex flex-wrap gap-2">
              {summary.map(s => (
                <button key={s.city} type="button" onClick={() => setLocDetail(s.city)} title={`View all ${s.count} marks in ${s.city}`}
                  className="text-xs bg-white border border-slate-200 rounded px-2 py-1 text-slate-700 hover:border-emerald-400 hover:bg-emerald-50 cursor-pointer">
                  <b className="text-slate-900">{s.city}</b>{s.state ? `, ${s.state}` : ''} — {s.count} mark{s.count === 1 ? '' : 's'} · {s.employees.size} emp
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {locDetail && (
        <LocationDetailModal
          city={locDetail}
          rows={rows.filter(r => (r.city || r.district || r.area || 'Unknown') === locDetail)}
          onClose={() => setLocDetail(null)}
        />
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setPreview(null)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center text-slate-700 hover:bg-slate-100">
              <X size={16} />
            </button>
            <img src={preview} alt="Attendance selfie" className="w-full max-h-[80vh] object-contain rounded-lg bg-white" />
            <div className="mt-2 text-center">
              <a href={preview} target="_blank" rel="noreferrer" className="text-xs text-white/80 hover:text-white underline">Open full image in new tab</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const reqStatusChip = (s: string) => s==='Completed'||s==='Approved'?'bg-emerald-100 text-emerald-700':s==='Pending'?'bg-amber-100 text-amber-700':s==='Rejected'?'bg-rose-100 text-rose-700':'bg-blue-100 text-blue-700';
const reqPrioChip = (p: string) => p==='High'?'bg-rose-100 text-rose-700':p==='Medium'?'bg-amber-100 text-amber-700':'bg-slate-100 text-slate-600';

function RequirementsTab() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [action, setAction] = useState<{ kind: 'approve'|'reject'|'reopen'|'escalate'; row: any } | null>(null);
  const [showChain, setShowChain] = useState(false);
  const [historyId, setHistoryId] = useState<number | null>(null);
  const { data } = useQuery({
    queryKey: ['hr','reqs', statusFilter, priorityFilter],
    queryFn: async () => (await api.get('/hr/requirements', { params: { ...(statusFilter && { status: statusFilter }), ...(priorityFilter && { priority: priorityFilter }) } })).data as any[],
  });
  const { data: summary } = useQuery({ queryKey: ['hr','reqs-summary'], queryFn: async () => (await api.get('/hr/requirements/summary')).data });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['hr','reqs'] }); qc.invalidateQueries({ queryKey: ['hr','reqs-summary'] }); };
  const save = useMutation({
    mutationFn: async (raw: any) => {
      // Coerce all numeric fields — number inputs in React store strings, but the API needs Int/Decimal
      const d: any = { ...raw };
      if (d.quantity !== undefined && d.quantity !== '') d.quantity = Number(d.quantity);
      if (d.tatDays !== undefined && d.tatDays !== '') d.tatDays = Number(d.tatDays);
      if (d.expectedExpense !== undefined && d.expectedExpense !== '') d.expectedExpense = Number(d.expectedExpense);
      if (d.actualExpense !== undefined && d.actualExpense !== '') d.actualExpense = Number(d.actualExpense);
      // Strip empties — Prisma rejects '' on optional Int/Decimal columns
      for (const k of ['quantity','tatDays','expectedExpense','actualExpense','employeeId']) {
        if (d[k] === '' || Number.isNaN(d[k])) delete d[k];
      }
      // Required field — validate client-side too so user sees a clear message instead of a 400
      if (!d.requirement || !String(d.requirement).trim()) throw new Error('Requirement is required');
      return d.id
        ? (await api.patch(`/hr/requirements/${d.id}`, d)).data
        : (await api.post('/hr/requirements', d)).data;
    },
    onSuccess: () => { invalidate(); setShowForm(false); setEditing(null); toast.success('Saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Save failed — check fields'),
  });
  const del = useMutation({ mutationFn: async (id: number) => (await api.delete(`/hr/requirements/${id}`)).data, onSuccess: () => { invalidate(); toast.success('Deleted'); } });
  const act = useMutation({
    mutationFn: async (v: { kind: string; id: number; body: any }) => (await api.post(`/hr/requirements/${v.id}/${v.kind}`, v.body)).data,
    onSuccess: (_r, v) => { invalidate(); setAction(null); toast.success(v.kind==='approve'?'Approved':v.kind==='reject'?'Rejected':v.kind==='escalate'?'Escalated to the next approver':'Re-opened'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Action failed'),
  });
  const byStatus: Record<string, number> = Object.fromEntries((summary?.byStatus ?? []).map((s:any)=>[s.status, s.count]));
  const byPriority: Record<string, number> = Object.fromEntries((summary?.byPriority ?? []).map((p:any)=>[p.priority, p.count]));
  const filtered = (data ?? []).filter((r: any) => inDateRange(r.date, from, to));
  const { slice: rows, page, setPage, pageSize, setPageSize, total } = usePaged(filtered, [statusFilter, priorityFilter, from, to]);
  return (
    <div>
      {/* Status + priority summary shown BEFORE the list; chips act as quick filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs font-semibold text-slate-500 uppercase mr-1">Status</span>
        {['Pending','Approved','In-Progress','Completed','Rejected'].map(s => (
          <button key={s} onClick={()=>setStatusFilter(statusFilter===s?'':s)} className={`text-xs px-2 py-1 rounded border ${statusFilter===s?'ring-2 ring-primary':''} ${reqStatusChip(s)}`}>{s} <b>{byStatus[s] ?? 0}</b></button>
        ))}
        <span className="text-xs font-semibold text-slate-500 uppercase mx-1">Priority</span>
        {['High','Medium','Low'].map(p => (
          <button key={p} onClick={()=>setPriorityFilter(priorityFilter===p?'':p)} className={`text-xs px-2 py-1 rounded border ${priorityFilter===p?'ring-2 ring-primary':''} ${reqPrioChip(p)}`}>{p} <b>{byPriority[p] ?? 0}</b></button>
        ))}
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} label="Date" />
        {(statusFilter || priorityFilter || from || to) && <button onClick={()=>{setStatusFilter('');setPriorityFilter('');setFrom('');setTo('');}} className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600">Clear filters ✕</button>}
      </div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-slate-500">{total} requirement{total === 1 ? '' : 's'}{(statusFilter||priorityFilter||from||to)?' (filtered)':''} · {summary?.total ?? 0} total</div>
        <div className="flex gap-2 items-center">
        <ImportExportBar
          rows={filtered.map((r: any) => ({ date: r.date?.slice(0,10), employeeName: r.employeeName ?? r.employee?.employeeName, empCode: r.empCode, department: r.department, requirement: r.requirement, requirementDetails: r.requirementDetails, quantity: r.quantity, priority: r.priority, status: r.status, expectedCompletionDate: r.expectedCompletionDate?.slice(0,10), approvedBy: r.approvedBy, rejectedBy: r.rejectedBy, rejectionReason: r.rejectionReason, tatDays: r.tatDays, expectedExpense: r.expectedExpense, actualExpense: r.actualExpense, remarks: r.remarks }))}
          headers={['date','employeeName','empCode','department','requirement','requirementDetails','quantity','priority','status','expectedCompletionDate','approvedBy','rejectedBy','rejectionReason','tatDays','expectedExpense','actualExpense','remarks']}
          filename="hr-requirements"
          onImport={async (rows) => {
            for (let i = 0; i < rows.length; i++) {
              const r = rows[i];
              if (!r.requirement || !String(r.requirement).trim()) throw new Error(`Row ${i + 1}: "requirement" is required (column missing or blank)`);
              try {
                await api.post('/hr/requirements', r);
              } catch (err: any) {
                const msg = err?.response?.data?.message ?? err?.message ?? 'request failed';
                throw new Error(`Row ${i + 1} (${r.requirement}): ${Array.isArray(msg) ? msg.join(', ') : msg}`);
              }
            }
            invalidate();
          }}
        />
        <button onClick={() => setShowChain(true)} className="border border-slate-300 text-slate-700 px-3 py-1.5 text-sm rounded flex items-center gap-1" title="Configure who approves requests above the Team Manager"><Settings2 size={14} /> Approval Chain</button>
        <button onClick={() => { setEditing({ requestType: 'Asset', status: 'Pending', priority: 'Medium', quantity: 1, date: new Date().toISOString().slice(0,10) }); setShowForm(true); }} className="bg-primary text-white px-3 py-1.5 text-sm rounded flex items-center gap-1"><Plus size={14} /> New Requirement</button>
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Emp</th><th className="text-left px-3 py-2">Dept</th>
              <th className="text-left px-3 py-2">Requirement</th><th className="text-left px-3 py-2">Qty</th><th className="text-left px-3 py-2">Priority</th>
              <th className="text-left px-3 py-2">Status / Awaiting</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Expense</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-1.5 text-xs">{r.date ? new Date(r.date).toLocaleDateString() : ''}</td>
                <td className="px-3 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${r.requestType==='Stationery'?'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200':'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>{r.requestType ?? 'Asset'}</span></td>
                <td className="px-3 py-1.5">{r.employeeName ?? r.employee?.employeeName ?? '—'}</td>
                <td className="px-3 py-1.5">{r.department ?? '—'}</td>
                <td className="px-3 py-1.5"><div className="font-medium">{r.requirement}</div><div className="text-xs text-slate-500">{r.requirementDetails}</div>{r.status==='Rejected' && r.rejectionReason && <div className="text-xs text-rose-600 mt-0.5">✕ {r.rejectionReason}</div>}</td>
                <td className="px-3 py-1.5">{r.quantity}</td>
                <td className="px-3 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded ${reqPrioChip(r.priority)}`}>{r.priority}</span></td>
                <td className="px-3 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded ${reqStatusChip(r.status)}`}>{r.status}</span>{r.reopenCount>0 && <span className="text-[9px] text-slate-400 ml-1">re-opened ×{r.reopenCount}</span>}
                  {r.status==='Pending' && (r.currentApproverName ? <div className="text-[10px] text-slate-500 mt-0.5">→ {r.currentApproverName}{r.currentLevel>1 && ' (esc.)'}</div> : <div className="text-[10px] text-amber-600 mt-0.5">→ no approver set</div>)}</td>
                <td className="px-3 py-1.5 text-xs">{r.status==='Rejected'?r.rejectedBy:r.approvedBy}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{r.expectedExpense ? `₹${r.expectedExpense}` : ''}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  {r.status!=='Approved' && r.status!=='Completed' && r.status!=='Rejected' && r.status!=='Cancelled' && <button title="Approve" onClick={()=>setAction({kind:'approve',row:r})} className="text-emerald-600 mr-1.5"><Check size={13} className="inline" /></button>}
                  {r.status==='Pending' && <button title="Escalate to higher management" onClick={()=>setAction({kind:'escalate',row:r})} className="text-indigo-600 mr-1.5"><ArrowUpCircle size={13} className="inline" /></button>}
                  {r.status!=='Rejected' && r.status!=='Completed' && r.status!=='Cancelled' && <button title="Reject" onClick={()=>setAction({kind:'reject',row:r})} className="text-rose-600 mr-1.5"><Ban size={13} className="inline" /></button>}
                  {r.status==='Rejected' && <button title="Re-open" onClick={()=>setAction({kind:'reopen',row:r})} className="text-blue-600 mr-1.5"><RotateCcw size={13} className="inline" /></button>}
                  <button title="History" onClick={()=>setHistoryId(r.id)} className="text-slate-500 mr-1.5"><Clock size={13} className="inline" /></button>
                  <button title="Edit" onClick={() => { setEditing(r); setShowForm(true); }} className="text-primary mr-1.5"><Edit3 size={12} className="inline" /></button>
                  <button title="Delete" onClick={() => { if (confirm('Delete?')) del.mutate(r.id); }} className="text-red-600"><Trash2 size={12} className="inline" /></button>
                </td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">No requirements {(statusFilter||priorityFilter||from||to)?'match these filters':'yet'}</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
      {showForm && <ReqForm initial={editing} onSave={(d:any) => save.mutate(d)} onClose={() => { setShowForm(false); setEditing(null); }} />}
      {action && <ReqActionModal kind={action.kind} row={action.row} busy={act.isPending} onClose={()=>setAction(null)} onSubmit={(body:any)=>act.mutate({kind:action.kind, id:action.row.id, body})} />}
      {historyId!=null && <ReqHistoryModal id={historyId} onClose={()=>setHistoryId(null)} />}
      {showChain && <ApprovalChainModal onClose={()=>setShowChain(false)} />}
    </div>
  );
}

// Company-wide approval chain: Level 1 is always the employee's Team Manager; here HR adds the
// HIGHER levels (Ops Head, Director, …) a Team Manager can escalate up to, tagging a real approver.
function ApprovalChainModal({ onClose }: { onClose: ()=>void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any | null>(null);
  const { data: levels } = useQuery({ queryKey: ['hr','approval-levels'], queryFn: async () => (await api.get('/hr/approval-levels')).data as any[] });
  const { data: users } = useQuery({ queryKey: ['users','assignable'], queryFn: async () => (await api.get('/users/assignable')).data as any[] });
  const inval = () => qc.invalidateQueries({ queryKey: ['hr','approval-levels'] });
  const save = useMutation({
    mutationFn: async (d: any) => d.id ? (await api.patch(`/hr/approval-levels/${d.id}`, d)).data : (await api.post('/hr/approval-levels', d)).data,
    onSuccess: () => { inval(); setEditing(null); toast.success('Saved'); },
    onError: (e:any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });
  const del = useMutation({ mutationFn: async (id:number) => (await api.delete(`/hr/approval-levels/${id}`)).data, onSuccess: () => { inval(); toast.success('Removed'); } });
  const toggle = useMutation({ mutationFn: async (l:any) => (await api.patch(`/hr/approval-levels/${l.id}`, { isActive: !l.isActive })).data, onSuccess: inval });
  const userOptions = (users ?? []).map((u:any) => ({ value: u.id, label: `${u.name}${u.empCode?` (${u.empCode})`:''}` }));

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center pt-16 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[640px] p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex justify-between items-center mb-1"><h2 className="text-lg font-bold flex items-center gap-2"><Settings2 size={18} /> Approval Chain</h2><button onClick={onClose}><X size={18} /></button></div>
        <p className="text-sm text-slate-500 mb-4">Every request goes to the employee&apos;s <b>Team Manager</b> first (Level 1). The Team Manager can approve, or <b>escalate</b> up these levels. Tag who approves at each level.</p>

        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 mb-4">
          <div className="flex items-center gap-3 px-3 py-2 bg-slate-50">
            <span className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-bold">1</span>
            <div className="flex-1"><div className="font-medium text-sm">Team Manager</div><div className="text-xs text-slate-500">Automatic — the employee&apos;s reporting manager (set on each Employee)</div></div>
            <span className="text-[10px] text-slate-400 uppercase">built-in</span>
          </div>
          {(levels ?? []).map((l:any) => (
            <div key={l.id} className={`flex items-center gap-3 px-3 py-2 ${l.isActive ? '' : 'opacity-50'}`}>
              <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">{l.level}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{l.title}</div>
                <div className="text-xs text-slate-500">{l.approverName ? `Approver: ${l.approverName}` : <span className="text-amber-600">No approver tagged — set one so escalations reach a person</span>}</div>
              </div>
              <button onClick={()=>toggle.mutate(l)} className={`text-[10px] px-2 py-0.5 rounded border ${l.isActive?'border-emerald-300 text-emerald-700':'border-slate-300 text-slate-500'}`}>{l.isActive?'Active':'Off'}</button>
              <button onClick={()=>setEditing(l)} className="text-primary" title="Edit"><Edit3 size={14} /></button>
              <button onClick={()=>{ if(confirm('Remove this level?')) del.mutate(l.id); }} className="text-rose-600" title="Remove"><Trash2 size={14} /></button>
            </div>
          ))}
          {(levels ?? []).length===0 && <div className="px-3 py-4 text-sm text-slate-400 text-center">No higher levels yet. Add one so Team Managers can escalate.</div>}
        </div>

        {editing ? (
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="text-sm font-semibold mb-2">{editing.id ? `Edit Level ${editing.level}` : 'Add Level'}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-[11px] text-slate-500 mb-0.5">Title *</label>
                <input value={editing.title ?? ''} onChange={e=>setEditing({...editing, title:e.target.value})} placeholder="e.g. Operations Head" className="w-full px-2 py-1 border rounded text-sm" /></div>
              <div><label className="block text-[11px] text-slate-500 mb-0.5">Approver (who signs off)</label>
                <SearchableSelect value={editing.approverUserId ?? ''} onChange={(v)=>setEditing({...editing, approverUserId: v===''?null:v})} options={userOptions} placeholder="— tag an approver —" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={()=>setEditing(null)} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
              <button disabled={!editing.title?.trim() || save.isPending} onClick={()=>save.mutate(editing)} className="bg-primary text-white px-4 py-1.5 text-sm rounded disabled:opacity-50">Save Level</button>
            </div>
          </div>
        ) : (
          <button onClick={()=>setEditing({ title:'', approverUserId:null })} className="text-sm text-primary flex items-center gap-1"><Plus size={15} /> Add a higher level</button>
        )}
      </div>
    </div>
  );
}

function ReqActionModal({ kind, row, onSubmit, onClose, busy }: { kind: 'approve'|'reject'|'reopen'|'escalate'; row: any; onSubmit: (b:any)=>void; onClose: ()=>void; busy: boolean }) {
  const [text, setText] = useState('');
  const isReject = kind === 'reject';
  const title = kind==='approve'?'Approve Requirement':kind==='reject'?'Reject Requirement':kind==='escalate'?'Escalate to Higher Management':'Re-open Requirement';
  const btnColor = kind==='approve'?'bg-emerald-600':kind==='reject'?'bg-rose-600':kind==='escalate'?'bg-indigo-600':'bg-blue-600';
  const btnLabel = kind==='escalate'?'Escalate':title.split(' ')[0];
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center pt-24">
      <div className="bg-white rounded-lg shadow-xl w-[460px] p-5">
        <div className="flex justify-between items-center mb-3"><h3 className="font-bold">{title}</h3><button onClick={onClose}><X size={18} /></button></div>
        <div className="text-sm text-slate-600 mb-3">{row.requirement}{(row.employeeName ?? row.employee?.employeeName) ? ` — ${row.employeeName ?? row.employee?.employeeName}` : ''}</div>
        {kind==='escalate' && <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded p-2 mb-3">Passes this request to the next approver in the company-wide chain. If no higher level is configured, you&apos;ll be asked to add one under <b>Approval Chain</b> or to Approve/Reject instead.</div>}
        <label className="block text-[11px] text-slate-500 mb-0.5">{isReject?'Rejection reason *':'Remarks (optional)'}</label>
        <textarea rows={3} value={text} onChange={e=>setText(e.target.value)} className="w-full px-2 py-1 border rounded text-sm" autoFocus placeholder={isReject?'Why is this being rejected?':'Any note for the record…'} />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
          <button disabled={busy || (isReject && !text.trim())} onClick={()=>onSubmit(isReject?{reason:text}:{remarks:text})} className={`px-4 py-1.5 text-sm rounded text-white disabled:opacity-50 ${btnColor}`}>{btnLabel}</button>
        </div>
      </div>
    </div>
  );
}

function ReqHistoryModal({ id, onClose }: { id: number; onClose: ()=>void }) {
  const { data } = useQuery({ queryKey: ['hr','req', id], queryFn: async () => (await api.get(`/hr/requirements/${id}`)).data });
  const hist: any[] = data?.history ?? [];
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center pt-20">
      <div className="bg-white rounded-lg shadow-xl w-[560px] p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-3"><h3 className="font-bold flex items-center gap-1"><Clock size={16} /> History — {data?.requirement ?? ''}</h3><button onClick={onClose}><X size={18} /></button></div>
        {hist.length===0 && <div className="text-sm text-slate-400 py-6 text-center">No history recorded yet</div>}
        <ol className="space-y-2">
          {hist.map((h:any) => (
            <li key={h.id} className="border-l-2 border-slate-200 pl-3 pb-1">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${reqStatusChip(h.toStatus || '')}`}>{h.action}</span>
                <span className="text-xs text-slate-500">{h.fromStatus?`${h.fromStatus} → `:''}{h.toStatus ?? ''}</span>
              </div>
              <div className="text-xs text-slate-600 mt-0.5">{h.actorName ?? 'System'} · {new Date(h.createdAt).toLocaleString()}</div>
              {h.remarks && <div className="text-xs text-slate-500 italic mt-0.5">“{h.remarks}”</div>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function ReqForm({ initial, onSave, onClose }: { initial: any; onSave: (d:any)=>void; onClose: ()=>void }) {
  const [d, setD] = useState<any>({ ...initial });
  // Active employees power both the Employee picker and the Assign-to-Manager picker.
  const { data: empData } = useQuery({
    queryKey: ['hr','emps','active-lite'],
    queryFn: async () => (await api.get('/hr/employees', { params: { status: 'Active', pageSize: 5000 } })).data?.items as any[],
  });
  const empList = empData ?? [];
  const empById = new Map<number, any>(empList.map((e:any) => [Number(e.id), e]));
  const empOptions = empList.map((e:any) => ({ value: Number(e.id), label: `${e.employeeName}${e.employeeId?` (${e.employeeId})`:''}` }));
  // The requirement is assigned to a Team Manager for approval. Default to the selected employee's own
  // reporting manager; the HR user can override it to any active employee.
  const empDefaultMgrId = d.employeeId ? empById.get(Number(d.employeeId))?.reportingManagerId ?? null : null;
  const assignManagerId = d.assignManagerId ?? d.reportingManagerId ?? empDefaultMgrId ?? '';
  const assignedMgr = assignManagerId ? empById.get(Number(assignManagerId)) : null;
  // A manager can't be their own approver.
  const mgrOptions = empOptions.filter((o:any) => Number(o.value) !== Number(d.employeeId));

  const pickEmployee = (id: any) => {
    const e = id !== '' ? empById.get(Number(id)) : null;
    setD((p:any) => ({
      ...p,
      employeeId: e ? Number(e.id) : '',
      employeeName: e ? e.employeeName : p.employeeName,
      empCode: e ? (e.employeeId ?? '') : p.empCode,
      // Reset the override so it re-defaults to the newly-picked employee's reporting manager.
      assignManagerId: e ? (e.reportingManagerId ?? '') : (p.assignManagerId ?? ''),
    }));
  };

  const F = (label: string, name: string, type='text', extra: any = {}) => (
    <div><label className="block text-[11px] text-slate-500 mb-0.5">{label}</label>
      <input type={type} {...extra} value={d[name] ?? ''} onChange={e => setD({...d,[name]:e.target.value})} className="w-full px-2 py-1 border rounded text-sm" /></div>
  );
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10">
      <div className="bg-white rounded-lg shadow-xl w-[760px] p-5">
        <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-bold">{d.id?'Edit':'New'} Requirement</h2><button onClick={onClose}><X size={18} /></button></div>
        <div className="grid grid-cols-3 gap-3">
          {F('Date','date','date')}
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Type</label>
            <select value={d.requestType ?? 'Asset'} onChange={e=>setD({...d,requestType:e.target.value})} className="w-full px-2 py-1 border rounded text-sm"><option>Asset</option><option>Stationery</option></select></div>
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Priority</label>
            <select value={d.priority ?? 'Medium'} onChange={e=>setD({...d,priority:e.target.value})} className="w-full px-2 py-1 border rounded text-sm"><option>High</option><option>Medium</option><option>Low</option></select></div>
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Employee</label>
            <SearchableSelect value={d.employeeId ?? ''} onChange={pickEmployee} options={empOptions} placeholder="— select employee (name / emp id) —" />
            {d.empCode && <div className="text-[10px] text-slate-400 mt-0.5">Emp ID: {d.empCode}</div>}</div>
          {F('Department','department')}
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Assign to (Team Manager)</label>
            <SearchableSelect value={assignManagerId} onChange={(v)=>setD({...d, assignManagerId: v === '' ? '' : Number(v)})} options={mgrOptions} placeholder="— select manager —" />
            <div className="text-[10px] mt-0.5 text-slate-400">{assignedMgr ? <>Goes to <b className="text-slate-500">{assignedMgr.employeeName}</b> for approval</> : 'No manager set — assign one so it enters the approval chain'}</div></div>
          {F('Requirement *','requirement')}
          {F('Quantity','quantity','number')}
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Status</label>
            <select value={d.status ?? 'Pending'} onChange={e=>setD({...d,status:e.target.value})} className="w-full px-2 py-1 border rounded text-sm"><option>Pending</option><option>Approved</option><option>In-Progress</option><option>Completed</option><option>Rejected</option></select></div>
          {F('Expected Completion','expectedCompletionDate','date')}
          {F('Approved By','approvedBy')}
          {F('TAT (days)','tatDays','number')}
          {F('Expected Expense','expectedExpense','number')}
        </div>
        <div className="mt-3"><label className="block text-[11px] text-slate-500 mb-0.5">Requirement Details</label>
          <textarea rows={2} value={d.requirementDetails ?? ''} onChange={e=>setD({...d,requirementDetails:e.target.value})} className="w-full px-2 py-1 border rounded text-sm" /></div>
        <div className="mt-3"><label className="block text-[11px] text-slate-500 mb-0.5">Remarks</label>
          <textarea rows={2} value={d.remarks ?? ''} onChange={e=>setD({...d,remarks:e.target.value})} className="w-full px-2 py-1 border rounded text-sm" /></div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
          <button onClick={()=>onSave(d)} className="bg-primary text-white px-4 py-1.5 text-sm rounded">Save</button>
        </div>
      </div>
    </div>
  );
}

function StockTab() {
  const qc = useQueryClient();
  const { data: items } = useQuery({ queryKey: ['hr','stock','items'], queryFn: async () => (await api.get('/hr/stock/items')).data as any[] });
  const { data: moves } = useQuery({ queryKey: ['hr','stock','movements'], queryFn: async () => (await api.get('/hr/stock/movements')).data as any[] });
  const { data: activeEmps } = useQuery({ queryKey: ['hr','emps','active-lite'], queryFn: async () => (await api.get('/hr/employees', { params: { status: 'Active', pageSize: 2000 } })).data?.items as any[] });
  const addItem = useMutation({ mutationFn: async (d:any) => (await api.post('/hr/stock/items', d)).data, onSuccess: () => qc.invalidateQueries({ queryKey: ['hr','stock'] }) });
  const addMove = useMutation({ mutationFn: async (d:any) => (await api.post('/hr/stock/movements', d)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','stock'] }); toast.success('Recorded'); },
    onError: (e:any) => toast.error(e?.response?.data?.message ?? 'Failed to record') });
  const delItem = useMutation({ mutationFn: async (id:number) => (await api.delete(`/hr/stock/items/${id}`)).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','stock'] }); toast.success('Item deleted'); }, onError: (e:any) => toast.error(e?.response?.data?.message ?? 'Delete failed') });
  const delMove = useMutation({ mutationFn: async (id:number) => (await api.delete(`/hr/stock/movements/${id}`)).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','stock'] }); toast.success('Movement deleted'); }, onError: (e:any) => toast.error(e?.response?.data?.message ?? 'Delete failed') });
  const [newItem, setNewItem] = useState({ itemName: '', category: '', unit: 'pieces', currentStock: 0 });
  // Stationery IN — invoice + photo are mandatory
  const [inMv, setInMv] = useState<any>({ itemId: '', qty: 1, remarks: '', invoiceUrl: '', photoUrl: '' });
  // Stationery OUT — separate Issued-To (active employee) + Remarks
  const [outMv, setOutMv] = useState<any>({ itemId: '', qty: 1, issuedTo: '', remarks: '' });
  const emps = activeEmps ?? [];
  const recordIn = () => addMove.mutate({ itemId: Number(inMv.itemId), stockIn: Number(inMv.qty || 0), remarks: inMv.remarks, invoiceUrl: inMv.invoiceUrl, photoUrl: inMv.photoUrl },
    { onSuccess: () => setInMv({ itemId: '', qty: 1, remarks: '', invoiceUrl: '', photoUrl: '' }) } as any);
  const recordOut = () => addMove.mutate({ itemId: Number(outMv.itemId), stockOut: Number(outMv.qty || 0), issuedTo: outMv.issuedTo, remarks: outMv.remarks },
    { onSuccess: () => setOutMv({ itemId: '', qty: 1, issuedTo: '', remarks: '' }) } as any);
  const inReady = inMv.itemId && Number(inMv.qty) > 0 && inMv.invoiceUrl && inMv.photoUrl;
  const outReady = outMv.itemId && Number(outMv.qty) > 0 && outMv.issuedTo;
  return (
    <div>
    <div className="flex justify-end gap-3 mb-3">
      <ImportExportBar
        rows={items ?? []}
        headers={['itemName','category','unit','currentStock','minStockAlert']}
        filename="hr-stock-items"
        onImport={async (rows) => { for (const r of rows) await api.post('/hr/stock/items', { ...r, currentStock: Number(r.currentStock||0), minStockAlert: r.minStockAlert ? Number(r.minStockAlert) : null }); qc.invalidateQueries({ queryKey: ['hr','stock','items'] }); }}
      />
      <ImportExportBar
        rows={(moves ?? []).map((m: any) => ({ movementDate: m.movementDate?.slice(0,10), itemName: m.item?.itemName, itemId: m.itemId, openingStock: m.openingStock, stockIn: m.stockIn, stockOut: m.stockOut, closingStock: m.closingStock, issuedTo: m.issuedTo, remarks: m.remarks }))}
        headers={['movementDate','itemName','itemId','openingStock','stockIn','stockOut','closingStock','issuedTo','remarks']}
        filename="hr-stock-movements"
        onImport={async (rows) => {
          const byName = new Map<string, number>((items ?? []).map((i: any) => [String(i.itemName).toLowerCase(), i.id]));
          for (const r of rows) {
            const itemId = Number(r.itemId) || byName.get(String(r.itemName || '').toLowerCase());
            if (!itemId) continue;
            // Bulk import is exempt from the invoice/photo requirement (historical rows).
            await api.post('/hr/stock/movements', { itemId, movementDate: r.movementDate, stockIn: Number(r.stockIn||0), stockOut: Number(r.stockOut||0), issuedTo: r.issuedTo, remarks: r.remarks, skipUploadCheck: true });
          }
          qc.invalidateQueries({ queryKey: ['hr','stock'] });
        }}
      />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <h3 className="font-semibold mb-2">Stock Items</h3>
        <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Unit</th><th className="text-right px-3 py-2">Current</th><th className="text-right px-3 py-2">Actions</th></tr></thead>
            <tbody>
              {(items ?? []).map(i => <tr key={i.id} className="border-t"><td className="px-3 py-1.5">{i.itemName}</td><td className="px-3 py-1.5">{i.category}</td><td className="px-3 py-1.5">{i.unit}</td><td className="px-3 py-1.5 text-right font-mono">{i.currentStock}</td><td className="px-3 py-1.5 text-right"><button onClick={() => { if (confirm(`Delete "${i.itemName}" and ALL its movements?`)) delItem.mutate(i.id); }} className="text-red-600" title="Delete item"><Trash2 size={12} className="inline" /></button></td></tr>)}
            </tbody>
          </table>
        </div>
        <div className="border border-slate-200 rounded-lg p-3 bg-white">
          <div className="text-xs font-semibold mb-2">Add new item</div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input placeholder="Item name" value={newItem.itemName} onChange={e=>setNewItem({...newItem,itemName:e.target.value})} className="px-2 py-1 border rounded text-sm" />
            <input placeholder="Category (Stationery/Cleaning)" value={newItem.category} onChange={e=>setNewItem({...newItem,category:e.target.value})} className="px-2 py-1 border rounded text-sm" />
            <input placeholder="Unit" value={newItem.unit} onChange={e=>setNewItem({...newItem,unit:e.target.value})} className="px-2 py-1 border rounded text-sm" />
            <input type="number" placeholder="Starting stock" value={newItem.currentStock} onChange={e=>setNewItem({...newItem,currentStock:Number(e.target.value)})} className="px-2 py-1 border rounded text-sm" />
          </div>
          <button onClick={()=>addItem.mutate(newItem)} className="bg-primary text-white px-3 py-1 text-sm rounded">Add Item</button>
        </div>
      </div>
      <div className="space-y-3">
        {/* Stationery IN */}
        <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/40">
          <h3 className="font-semibold mb-2 text-sm text-emerald-800">Stationery In (Receive Stock)</h3>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <select value={inMv.itemId} onChange={e=>setInMv({...inMv,itemId:e.target.value})} className="px-2 py-1 border rounded text-sm bg-white">
              <option value="">— item —</option>
              {(items ?? []).map(i => <option key={i.id} value={i.id}>{i.itemName}</option>)}
            </select>
            <input type="number" min={1} placeholder="Quantity in" value={inMv.qty} onChange={e=>setInMv({...inMv,qty:e.target.value})} className="px-2 py-1 border rounded text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <DocUpload label="Invoice" required value={inMv.invoiceUrl || undefined} onChange={(u)=>setInMv({...inMv,invoiceUrl:u ?? ''})} />
            <DocUpload label="Items photo" required value={inMv.photoUrl || undefined} onChange={(u)=>setInMv({...inMv,photoUrl:u ?? ''})} />
          </div>
          <input placeholder="Remarks (optional)" value={inMv.remarks} onChange={e=>setInMv({...inMv,remarks:e.target.value})} className="px-2 py-1 border rounded text-sm w-full mb-2" />
          <button onClick={recordIn} disabled={!inReady || addMove.isPending} className="bg-emerald-600 text-white px-3 py-1 text-sm rounded disabled:opacity-50">Record In</button>
          {!inReady && <span className="text-[11px] text-slate-500 ml-2">Item, quantity, invoice &amp; photo are required.</span>}
        </div>
        {/* Stationery OUT */}
        <div className="border border-rose-200 rounded-lg p-3 bg-rose-50/40">
          <h3 className="font-semibold mb-2 text-sm text-rose-800">Stationery Out (Issue Stock)</h3>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <select value={outMv.itemId} onChange={e=>setOutMv({...outMv,itemId:e.target.value})} className="px-2 py-1 border rounded text-sm bg-white">
              <option value="">— item —</option>
              {(items ?? []).map(i => <option key={i.id} value={i.id}>{i.itemName}</option>)}
            </select>
            <input type="number" min={1} placeholder="Quantity out" value={outMv.qty} onChange={e=>setOutMv({...outMv,qty:e.target.value})} className="px-2 py-1 border rounded text-sm" />
            <select value={outMv.issuedTo} onChange={e=>setOutMv({...outMv,issuedTo:e.target.value})} className="px-2 py-1 border rounded text-sm bg-white">
              <option value="">— Issued to (active employee) —</option>
              {emps.map((e:any) => <option key={e.id} value={e.employeeName}>{e.employeeName}{e.employeeId?` (${e.employeeId})`:''}</option>)}
            </select>
            <input placeholder="Remarks (optional)" value={outMv.remarks} onChange={e=>setOutMv({...outMv,remarks:e.target.value})} className="px-2 py-1 border rounded text-sm" />
          </div>
          <button onClick={recordOut} disabled={!outReady || addMove.isPending} className="bg-rose-600 text-white px-3 py-1 text-sm rounded disabled:opacity-50">Record Out</button>
          {!outReady && <span className="text-[11px] text-slate-500 ml-2">Item, quantity &amp; issued-to are required.</span>}
        </div>
        <h3 className="font-semibold mb-2 text-sm">Recent Movements</h3>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 uppercase"><tr><th className="text-left px-2 py-1.5">Date</th><th className="text-left px-2 py-1.5">Item</th><th className="text-right px-2 py-1.5">In</th><th className="text-right px-2 py-1.5">Out</th><th className="text-right px-2 py-1.5">Close</th><th className="text-left px-2 py-1.5">Issued To</th><th className="text-left px-2 py-1.5">Remarks</th><th className="text-left px-2 py-1.5">Docs</th><th className="text-right px-2 py-1.5">Actions</th></tr></thead>
            <tbody>
              {(moves ?? []).slice(0,30).map(m => <tr key={m.id} className="border-t"><td className="px-2 py-1">{new Date(m.movementDate).toLocaleDateString()}</td><td className="px-2 py-1">{m.item?.itemName}</td><td className="px-2 py-1 text-right font-mono text-emerald-700">{m.stockIn || ''}</td><td className="px-2 py-1 text-right font-mono text-rose-700">{m.stockOut || ''}</td><td className="px-2 py-1 text-right font-mono font-bold">{m.closingStock}</td><td className="px-2 py-1">{m.issuedTo ?? '—'}</td><td className="px-2 py-1">{m.remarks ?? '—'}</td><td className="px-2 py-1 space-x-1">{m.invoiceUrl && <a href={m.invoiceUrl} target="_blank" rel="noreferrer" className="text-primary underline">Invoice</a>}{m.photoUrl && <a href={m.photoUrl} target="_blank" rel="noreferrer" className="text-primary underline">Photo</a>}{!m.invoiceUrl && !m.photoUrl && '—'}</td><td className="px-2 py-1 text-right"><button onClick={() => { if (confirm('Delete this movement? Stock will be adjusted.')) delMove.mutate(m.id); }} className="text-red-600" title="Delete movement"><Trash2 size={12} className="inline" /></button></td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </div>
  );
}

function AssetsTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const { data } = useQuery({ queryKey: ['hr','assets', search, deviceType], queryFn: async () => (await api.get('/hr/assets', { params: { search, deviceType } })).data as any[] });
  const filtered = (data ?? []).filter((a: any) => inDateRange(a.dateOfIssue, from, to));
  const { slice: rows, page, setPage, pageSize, setPageSize, total } = usePaged(filtered, [search, deviceType, from, to]);
  const save = useMutation({ mutationFn: async (d:any) => d.id ? (await api.patch(`/hr/assets/${d.id}`, d)).data : (await api.post('/hr/assets', d)).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','assets'] }); setShowForm(false); setEditing(null); toast.success('Saved'); } });
  const del = useMutation({ mutationFn: async (id:number) => (await api.delete(`/hr/assets/${id}`)).data, onSuccess: () => qc.invalidateQueries({ queryKey: ['hr','assets'] }) });
  const [view, setView] = useState<'issued'|'requests'>('issued');
  const tabCls = (active: boolean) => `px-3 py-1.5 text-sm rounded ${active ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'}`;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button onClick={()=>setView('issued')} className={tabCls(view==='issued')}>Issued Assets</button>
        <button onClick={()=>setView('requests')} className={tabCls(view==='requests')}>Asset Requests</button>
      </div>
      {view === 'requests' ? <AssetRequestsPanel /> : (<>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input placeholder="Search by name/code/MAC/asset tag" value={search} onChange={e=>setSearch(e.target.value)} className="px-3 py-1.5 border rounded text-sm w-72" />
        <select value={deviceType} onChange={e=>setDeviceType(e.target.value)} className="px-3 py-1.5 border rounded text-sm">
          <option value="">All types</option>
          {['Laptop','Computer','Sim','Printer','Vehicle','Mobile','Other'].map(t => <option key={t}>{t}</option>)}
        </select>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} label="Issued" />
        <div className="ml-auto flex gap-2 items-center">
        <ImportExportBar
          rows={filtered.map((a: any) => ({ employeeName: a.employeeName, empCode: a.empCode, branch: a.branch, groupName: a.groupName, deviceType: a.deviceType, deviceId: a.deviceId, macId: a.macId, configuration: a.configuration, modelName: a.modelName, brand: a.brand, cpu: a.cpu, assetTagId: a.assetTagId, dateOfIssue: a.dateOfIssue?.slice(0,10), dateOfPurchase: a.dateOfPurchase?.slice(0,10), verified: a.verified, matched: a.matched, status: a.status }))}
          headers={['employeeName','empCode','branch','groupName','deviceType','deviceId','macId','configuration','modelName','brand','cpu','assetTagId','dateOfIssue','dateOfPurchase','verified','matched','status']}
          filename="hr-issued-assets"
          onImport={async (rows) => { for (const r of rows) { const body: any = { ...r }; if (body.verified !== undefined) body.verified = String(body.verified).toLowerCase() === 'true'; if (body.matched !== undefined) body.matched = String(body.matched).toLowerCase() === 'true'; await api.post('/hr/assets', body); } qc.invalidateQueries({ queryKey: ['hr','assets'] }); }}
        />
        <button onClick={()=>{ setEditing({ status:'Issued' }); setShowForm(true); }} className="bg-primary text-white px-3 py-1.5 text-sm rounded flex items-center gap-1"><Plus size={14} /> Issue Asset</button>
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr><th className="text-left px-3 py-2">Emp</th><th className="text-left px-3 py-2">Branch</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Device ID</th><th className="text-left px-3 py-2">MAC</th><th className="text-left px-3 py-2">Model</th><th className="text-left px-3 py-2">Issued</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((a:any) => <tr key={a.id} className="border-t">
              <td className="px-3 py-1.5"><div className="font-medium text-xs">{a.employeeName}</div><div className="font-mono text-[10px] text-slate-500">{a.empCode}</div></td>
              <td className="px-3 py-1.5">{a.branch}</td><td className="px-3 py-1.5">{a.deviceType}</td>
              <td className="px-3 py-1.5 font-mono text-[11px]">{a.deviceId}</td><td className="px-3 py-1.5 font-mono text-[11px]">{a.macId}</td>
              <td className="px-3 py-1.5">{a.modelName}</td><td className="px-3 py-1.5 text-xs">{a.dateOfIssue ? new Date(a.dateOfIssue).toLocaleDateString() : ''}</td>
              <td className="px-3 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded ${a.status==='Issued'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-600'}`}>{a.status}</span></td>
              <td className="px-3 py-1.5 text-right">
                <button onClick={()=>{ setEditing(a); setShowForm(true); }} className="text-primary mr-2"><Edit3 size={12} className="inline" /></button>
                <button onClick={()=>{ if(confirm('Delete?')) del.mutate(a.id); }} className="text-red-600"><Trash2 size={12} className="inline" /></button>
              </td>
            </tr>)}
            {rows.length===0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400">No assets{(search||deviceType||from||to)?' match these filters':''}</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
      </>)}
      {showForm && <AssetForm initial={editing} onSave={(d:any) => save.mutate(d)} onClose={() => { setShowForm(false); setEditing(null); }} />}
    </div>
  );
}

// Employee asset requests → reporting-manager approval → issue.
function AssetRequestsPanel() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [issuing, setIssuing] = useState<any | null>(null);
  const { data: reqs } = useQuery({ queryKey: ['hr','asset-requests', status], queryFn: async () => (await api.get('/hr/asset-requests', { params: { status } })).data as any[] });
  const { data: emps } = useQuery({ queryKey: ['hr','emps','active-lite'], queryFn: async () => (await api.get('/hr/employees', { params: { status: 'Active', pageSize: 2000 } })).data?.items as any[] });
  const create = useMutation({ mutationFn: async (d:any) => (await api.post('/hr/asset-requests', d)).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','asset-requests'] }); setShowForm(false); toast.success('Request submitted'); }, onError: (e:any)=>toast.error(e?.response?.data?.message ?? 'Failed') });
  const approve = useMutation({ mutationFn: async (id:number) => (await api.post(`/hr/asset-requests/${id}/approve`, {})).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','asset-requests'] }); toast.success('Approved'); }, onError: (e:any)=>toast.error(e?.response?.data?.message ?? 'Failed') });
  const reject = useMutation({ mutationFn: async ({id,reason}:{id:number;reason:string}) => (await api.post(`/hr/asset-requests/${id}/reject`, { reason })).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','asset-requests'] }); toast.success('Rejected'); }, onError: (e:any)=>toast.error(e?.response?.data?.message ?? 'Failed') });
  const del = useMutation({ mutationFn: async (id:number) => (await api.delete(`/hr/asset-requests/${id}`)).data, onSuccess: () => qc.invalidateQueries({ queryKey: ['hr','asset-requests'] }) });
  const badge = (s:string) => s==='Approved'?'bg-emerald-100 text-emerald-700':s==='Rejected'?'bg-rose-100 text-rose-700':s==='Issued'?'bg-indigo-100 text-indigo-700':'bg-amber-100 text-amber-700';
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <select value={status} onChange={e=>setStatus(e.target.value)} className="px-3 py-1.5 border rounded text-sm">
          <option value="">All statuses</option>
          {['Pending','Approved','Rejected','Issued'].map(s=><option key={s}>{s}</option>)}
        </select>
        <button onClick={()=>setShowForm(true)} className="ml-auto bg-primary text-white px-3 py-1.5 text-sm rounded flex items-center gap-1"><Plus size={14}/> New Request</button>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Device</th><th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">Reporting Manager</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Decision</th><th className="text-right px-3 py-2">Actions</th></tr></thead>
          <tbody>
            {(reqs ?? []).map((r:any) => <tr key={r.id} className="border-t align-top">
              <td className="px-3 py-1.5"><div className="font-medium text-xs">{r.employeeName ?? r.employee?.employeeName ?? '—'}</div><div className="font-mono text-[10px] text-slate-500">{r.empCode}</div></td>
              <td className="px-3 py-1.5">{r.deviceType}</td>
              <td className="px-3 py-1.5 text-xs max-w-[16rem]">{r.reason ?? '—'}</td>
              <td className="px-3 py-1.5 text-xs">{r.reportingManagerName ?? '—'}</td>
              <td className="px-3 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded ${badge(r.status)}`}>{r.status}</span></td>
              <td className="px-3 py-1.5 text-[11px] text-slate-500">{r.approvedByName ? <div>{r.approvedByName}{r.decisionRemarks?` — ${r.decisionRemarks}`:''}</div> : '—'}</td>
              <td className="px-3 py-1.5 text-right whitespace-nowrap">
                {r.status==='Pending' && <>
                  <button onClick={()=>approve.mutate(r.id)} className="text-emerald-700 mr-2" title="Approve"><Check size={14} className="inline"/></button>
                  <button onClick={()=>{ const reason=prompt('Reason for rejection?'); if(reason && reason.trim()) reject.mutate({id:r.id,reason:reason.trim()}); }} className="text-rose-600 mr-2" title="Reject"><Ban size={14} className="inline"/></button>
                </>}
                {r.status==='Approved' && <button onClick={()=>setIssuing(r)} className="text-indigo-700 mr-2 text-xs font-medium" title="Issue asset">Issue</button>}
                <button onClick={()=>{ if(confirm('Delete this request?')) del.mutate(r.id); }} className="text-red-600" title="Delete"><Trash2 size={12} className="inline"/></button>
              </td>
            </tr>)}
            {(reqs ?? []).length===0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No asset requests</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <AssetRequestForm emps={emps ?? []} onSave={(d:any)=>create.mutate(d)} onClose={()=>setShowForm(false)} />}
      {issuing && <IssueFromRequestModal req={issuing} onClose={()=>setIssuing(null)} onDone={()=>{ setIssuing(null); qc.invalidateQueries({ queryKey: ['hr','asset-requests'] }); qc.invalidateQueries({ queryKey: ['hr','assets'] }); }} />}
    </div>
  );
}

function AssetRequestForm({ emps, onSave, onClose }: { emps:any[]; onSave:(d:any)=>void; onClose:()=>void }) {
  const [d, setD] = useState<any>({ employeeId:'', deviceType:'', reason:'', reportingManagerId:'' });
  const ready = d.employeeId && d.deviceType && d.reportingManagerId;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10">
      <div className="bg-white rounded-lg shadow-xl w-[560px] p-5">
        <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-bold">New Asset Request</h2><button onClick={onClose}><X size={18}/></button></div>
        <div className="space-y-3">
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Employee <span className="text-rose-600">*</span></label>
            <select value={d.employeeId} onChange={e=>setD({...d,employeeId:e.target.value})} className="w-full px-2 py-1 border rounded text-sm">
              <option value="">— select employee —</option>
              {emps.map((e:any)=><option key={e.id} value={e.id}>{e.employeeName}{e.employeeId?` (${e.employeeId})`:''}</option>)}
            </select></div>
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Device Type <span className="text-rose-600">*</span></label>
            <select value={d.deviceType} onChange={e=>setD({...d,deviceType:e.target.value})} className="w-full px-2 py-1 border rounded text-sm"><option value="">—</option>{['Laptop','Computer','Sim','Printer','Vehicle','Mobile','Other'].map(t=><option key={t}>{t}</option>)}</select></div>
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Reporting Manager <span className="text-rose-600">*</span></label>
            <select value={d.reportingManagerId} onChange={e=>setD({...d,reportingManagerId:e.target.value})} className="w-full px-2 py-1 border rounded text-sm">
              <option value="">— select reporting manager —</option>
              {emps.map((e:any)=><option key={e.id} value={e.id}>{e.employeeName}{e.employeeId?` (${e.employeeId})`:''}</option>)}
            </select></div>
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Reason / justification</label>
            <textarea value={d.reason} onChange={e=>setD({...d,reason:e.target.value})} rows={2} className="w-full px-2 py-1 border rounded text-sm" placeholder="Why is this asset needed?" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
          <button onClick={()=>onSave(d)} disabled={!ready} className="bg-primary text-white px-4 py-1.5 text-sm rounded disabled:opacity-50">Submit Request</button>
        </div>
      </div>
    </div>
  );
}

function IssueFromRequestModal({ req, onClose, onDone }: { req:any; onClose:()=>void; onDone:()=>void }) {
  const [d, setD] = useState<any>({ deviceType: req.deviceType, deviceId:'', macId:'', brand:'', modelName:'', assetTagId:'', dateOfIssue:'', remarks:'' });
  const issue = useMutation({ mutationFn: async (body:any) => (await api.post(`/hr/asset-requests/${req.id}/issue`, body)).data,
    onSuccess: () => { toast.success('Asset issued'); onDone(); }, onError: (e:any)=>toast.error(e?.response?.data?.message ?? 'Failed to issue') });
  const F = (label:string,name:string,type='text') => (<div><label className="block text-[11px] text-slate-500 mb-0.5">{label}</label>
    <input type={type} value={d[name] ?? ''} onChange={e=>setD({...d,[name]:e.target.value})} className="w-full px-2 py-1 border rounded text-sm" /></div>);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10">
      <div className="bg-white rounded-lg shadow-xl w-[720px] p-5">
        <div className="flex justify-between items-center mb-1"><h2 className="text-lg font-bold">Issue Asset</h2><button onClick={onClose}><X size={18}/></button></div>
        <div className="text-xs text-slate-500 mb-3">For <b>{req.employeeName}</b> · {req.deviceType} · approved by {req.approvedByName ?? '—'}</div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Device Type</label>
            <select value={d.deviceType ?? ''} onChange={e=>setD({...d,deviceType:e.target.value})} className="w-full px-2 py-1 border rounded text-sm">{['Laptop','Computer','Sim','Printer','Vehicle','Mobile','Other'].map(t=><option key={t}>{t}</option>)}</select></div>
          {F('Device ID','deviceId')}{F('MAC ID','macId')}{F('Brand','brand')}{F('Model','modelName')}{F('Asset Tag ID','assetTagId')}{F('Date of Issue','dateOfIssue','date')}
        </div>
        <div className="mt-3"><label className="block text-[11px] text-slate-500 mb-0.5">Remarks <span className="text-rose-600">*</span></label>
          <textarea value={d.remarks} onChange={e=>setD({...d,remarks:e.target.value})} rows={2} className={`w-full px-2 py-1 border rounded text-sm ${!String(d.remarks??'').trim()?'border-rose-300 bg-rose-50':'border-slate-300'}`} placeholder="Condition / accessories / acknowledgement" /></div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
          <button onClick={()=>issue.mutate(d)} disabled={!String(d.remarks??'').trim() || issue.isPending} className="bg-primary text-white px-4 py-1.5 text-sm rounded disabled:opacity-50">Issue Asset</button>
        </div>
      </div>
    </div>
  );
}

function AssetForm({ initial, onSave, onClose }: { initial: any; onSave: (d:any)=>void; onClose: ()=>void }) {
  const [d, setD] = useState<any>({ ...initial });
  const F = (label: string, name: string, type='text') => (
    <div><label className="block text-[11px] text-slate-500 mb-0.5">{label}</label>
      <input type={type} value={d[name] ?? ''} onChange={e=>setD({...d,[name]:e.target.value})} className="w-full px-2 py-1 border rounded text-sm" /></div>
  );
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10">
      <div className="bg-white rounded-lg shadow-xl w-[900px] p-5">
        <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-bold">{d.id?'Edit':'Issue'} Asset</h2><button onClick={onClose}><X size={18} /></button></div>
        <div className="grid grid-cols-3 gap-3">
          {F('Employee Name','employeeName')}
          {F('Emp Code','empCode')}
          {F('Branch','branch')}
          {F('Group','groupName')}
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Device Type *</label>
            <select value={d.deviceType ?? ''} onChange={e=>setD({...d,deviceType:e.target.value})} className="w-full px-2 py-1 border rounded text-sm"><option value="">—</option>{['Laptop','Computer','Sim','Printer','Vehicle','Mobile','Other'].map(t=><option key={t}>{t}</option>)}</select></div>
          {F('Device ID','deviceId')}
          {F('MAC ID','macId')}
          {F('Brand','brand')}
          {F('Model','modelName')}
          {F('CPU','cpu')}
          {F('Configuration','configuration')}
          {F('Asset Tag ID','assetTagId')}
          {F('Date of Issue','dateOfIssue','date')}
          {F('Date of Purchase','dateOfPurchase','date')}
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Status</label>
            <select value={d.status ?? 'Issued'} onChange={e=>setD({...d,status:e.target.value})} className="w-full px-2 py-1 border rounded text-sm"><option>Issued</option><option>Returned</option><option>Damaged</option><option>Lost</option></select></div>
        </div>
        <div className="mt-3">
          <label className="block text-[11px] text-slate-500 mb-0.5">Remarks <span className="text-rose-600">*</span></label>
          <textarea value={d.remarks ?? ''} onChange={e=>setD({...d,remarks:e.target.value})} rows={2}
            placeholder="e.g. condition at handover, accessories included, acknowledgement"
            className={`w-full px-2 py-1 border rounded text-sm ${!String(d.remarks ?? '').trim() ? 'border-rose-300 bg-rose-50' : 'border-slate-300'}`} />
        </div>
        <div className="flex gap-3 mt-3">
          <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={!!d.verified} onChange={e=>setD({...d,verified:e.target.checked})} /> Verified</label>
          <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={!!d.matched} onChange={e=>setD({...d,matched:e.target.checked})} /> MAC matched</label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
          <button onClick={()=>onSave(d)} disabled={!String(d.remarks ?? '').trim()} className="bg-primary text-white px-4 py-1.5 text-sm rounded disabled:opacity-50">Save</button>
        </div>
      </div>
    </div>
  );
}

function RepairsTab() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data } = useQuery({ queryKey: ['hr','repairs'], queryFn: async () => (await api.get('/hr/repairs')).data as any[] });
  const save = useMutation({ mutationFn: async (d:any) => d.id ? (await api.patch(`/hr/repairs/${d.id}`, d)).data : (await api.post('/hr/repairs', d)).data, onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr','repairs'] }); setShowForm(false); setEditing(null); toast.success('Saved'); } });
  const del = useMutation({ mutationFn: async (id:number) => (await api.delete(`/hr/repairs/${id}`)).data, onSuccess: () => qc.invalidateQueries({ queryKey: ['hr','repairs'] }) });
  const filtered = (data ?? []).filter((r: any) => inDateRange(r.repairDate, from, to));
  const { slice: rows, page, setPage, pageSize, setPageSize, total } = usePaged(filtered, [from, to]);
  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <div className="flex items-center gap-3">
          <div className="text-sm text-slate-500">{total} repair entr{total === 1 ? 'y' : 'ies'}{(from||to)?' (filtered)':''}</div>
          <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} label="Repair date" />
        </div>
        <div className="flex gap-2 items-center">
        <ImportExportBar
          rows={filtered.map((r: any) => ({ deviceType: r.deviceType, userName: r.userName, department: r.department, brandModel: r.brandModel, macId: r.macId, issueOccurring: r.issueOccurring, repairDate: r.repairDate?.slice(0,10), vendor: r.vendor, partsChanged: r.partsChanged, partCost: r.partCost, warrantyStart: r.warrantyStart?.slice(0,10), warrantyEnd: r.warrantyEnd?.slice(0,10), repairStatus: r.repairStatus, remarks: r.remarks }))}
          headers={['deviceType','userName','department','brandModel','macId','issueOccurring','repairDate','vendor','partsChanged','partCost','warrantyStart','warrantyEnd','repairStatus','remarks']}
          filename="hr-repairs"
          onImport={async (rows) => { for (const r of rows) await api.post('/hr/repairs', r); qc.invalidateQueries({ queryKey: ['hr','repairs'] }); }}
        />
        <button onClick={()=>{ setEditing({ repairStatus: 'Pending' }); setShowForm(true); }} className="bg-primary text-white px-3 py-1.5 text-sm rounded flex items-center gap-1"><Plus size={14} /> Log Repair</button>
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Brand/Model</th><th className="text-left px-3 py-2">Issue</th><th className="text-left px-3 py-2">Parts</th><th className="text-left px-3 py-2">Cost</th><th className="text-left px-3 py-2">Warranty</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Actions</th></tr></thead>
          <tbody>
            {rows.map((r:any) => <tr key={r.id} className="border-t">
              <td className="px-3 py-1.5 text-xs">{r.repairDate ? new Date(r.repairDate).toLocaleDateString() : ''}</td>
              <td className="px-3 py-1.5">{r.deviceType}</td><td className="px-3 py-1.5">{r.userName}</td>
              <td className="px-3 py-1.5">{r.brandModel}</td><td className="px-3 py-1.5 text-xs">{r.issueOccurring}</td>
              <td className="px-3 py-1.5 text-xs">{r.partsChanged}</td><td className="px-3 py-1.5 font-mono text-xs">{r.partCost ? `₹${r.partCost}` : ''}</td>
              <td className="px-3 py-1.5 text-xs">{r.warrantyEnd ? new Date(r.warrantyEnd).toLocaleDateString() : '—'}</td>
              <td className="px-3 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded ${r.repairStatus==='Completed'?'bg-emerald-100 text-emerald-700':'bg-amber-100 text-amber-700'}`}>{r.repairStatus}</span></td>
              <td className="px-3 py-1.5 text-right">
                <button onClick={()=>{ setEditing(r); setShowForm(true); }} className="text-primary mr-2"><Edit3 size={12} className="inline" /></button>
                <button onClick={()=>{ if(confirm('Delete?')) del.mutate(r.id); }} className="text-red-600"><Trash2 size={12} className="inline" /></button>
              </td>
            </tr>)}
            {rows.length===0 && <tr><td colSpan={10} className="text-center py-8 text-slate-400">No repairs {(from||to)?'match these dates':'logged'}</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
      {showForm && <RepairForm initial={editing} onSave={(d:any) => save.mutate(d)} onClose={() => { setShowForm(false); setEditing(null); }} />}
    </div>
  );
}

function RepairForm({ initial, onSave, onClose }: { initial: any; onSave: (d:any)=>void; onClose: ()=>void }) {
  const [d, setD] = useState<any>({ ...initial });
  const F = (label: string, name: string, type='text') => (
    <div><label className="block text-[11px] text-slate-500 mb-0.5">{label}</label>
      <input type={type} value={d[name] ?? ''} onChange={e=>setD({...d,[name]:e.target.value})} className="w-full px-2 py-1 border rounded text-sm" /></div>
  );
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10">
      <div className="bg-white rounded-lg shadow-xl w-[800px] p-5">
        <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-bold">{d.id?'Edit':'Log'} Repair</h2><button onClick={onClose}><X size={18} /></button></div>
        <div className="grid grid-cols-3 gap-3">
          {F('Device Type','deviceType')}
          {F('User Name','userName')}
          {F('Department','department')}
          {F('Brand/Model','brandModel')}
          {F('MAC ID','macId')}
          {F('Vendor / Engineer','vendor')}
          {F('Parts Changed','partsChanged')}
          {F('Part Cost (₹)','partCost','number')}
          {F('Repair Date','repairDate','date')}
          {F('Warranty Start','warrantyStart','date')}
          {F('Warranty End','warrantyEnd','date')}
          <div><label className="block text-[11px] text-slate-500 mb-0.5">Status</label>
            <select value={d.repairStatus ?? 'Pending'} onChange={e=>setD({...d,repairStatus:e.target.value})} className="w-full px-2 py-1 border rounded text-sm"><option>Pending</option><option>Sent</option><option>Completed</option><option>Cancelled</option></select></div>
        </div>
        <div className="mt-3"><label className="block text-[11px] text-slate-500 mb-0.5">Issue Occurring</label>
          <textarea rows={2} value={d.issueOccurring ?? ''} onChange={e=>setD({...d,issueOccurring:e.target.value})} className="w-full px-2 py-1 border rounded text-sm" /></div>
        <div className="mt-3"><label className="block text-[11px] text-slate-500 mb-0.5">Remarks</label>
          <textarea rows={2} value={d.remarks ?? ''} onChange={e=>setD({...d,remarks:e.target.value})} className="w-full px-2 py-1 border rounded text-sm" /></div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
          <button onClick={()=>onSave(d)} className="bg-primary text-white px-4 py-1.5 text-sm rounded">Save</button>
        </div>
      </div>
    </div>
  );
}

function PfTab() {
  const qc = useQueryClient();
  const thisMonth = new Date().toISOString().slice(0,7);
  const [payMonth, setPayMonth] = useState(thisMonth + '-01');   // month the Generate button targets
  const [from, setFrom] = useState(thisMonth);                    // view range (YYYY-MM)
  const [to, setTo] = useState(thisMonth);
  const { data } = useQuery({ queryKey: ['hr','pf'], queryFn: async () => (await api.get('/hr/pf')).data as any[] });
  const filtered = (data ?? []).filter((r: any) => inMonthRange(r.payMonth, from, to));
  const { slice: rows, page, setPage, pageSize, setPageSize, total } = usePaged(filtered, [from, to]);
  const gen = useMutation({ mutationFn: async () => (await api.post('/hr/generate-pf-esi', { payMonth })).data, onSuccess: (r) => {
    toast.success(`Generated ${r.pfGenerated} PF + ${r.esiGenerated} ESI rows (pro-rated over ${r.daysInMonth} days)`);
    const noAtt: string[] = r.noAttendance ?? [];
    if (noAtt.length) toast.warning(`${noAtt.length} employee(s) had no attendance marked for this month — paid as a full month. Mark attendance first for accurate PF/ESI. (${noAtt.slice(0,8).join(', ')}${noAtt.length>8?'…':''})`, { duration: 10000 });
    qc.invalidateQueries({ queryKey: ['hr','pf'] }); qc.invalidateQueries({ queryKey: ['hr','esi'] });
  } });
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[11px] text-slate-500">Generate for</span>
        <input type="month" value={payMonth.slice(0,7)} onChange={e=>setPayMonth(e.target.value + '-01')} className="px-2 py-1 border rounded text-sm" />
        <button onClick={()=>gen.mutate()} className="bg-primary text-white px-3 py-1.5 text-sm rounded">Generate from salary</button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <MonthRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} label="Show" />
        <div className="text-sm text-slate-500">{total} row{total === 1 ? '' : 's'}</div>
        <div className="ml-auto">
          <ImportExportBar
            rows={filtered.map((r: any) => ({ employeeId: r.employee?.employeeId, employeeName: r.employee?.employeeName, payMonth: r.payMonth?.slice(0,10), uan: r.uan, esi: r.esi, fatherHusbandName: r.fatherHusbandName, edliWages: r.edliWages, basic: r.basic, hra: r.hra, transportAllowance: r.transportAllowance, otherAllowance: r.otherAllowance, telephoneAllowance: r.telephoneAllowance, totalFinal: r.totalFinal, employeeEpf12: r.employeeEpf12, employerPf367: r.employerPf367, employerPension833: r.employerPension833, adminCharges05: r.adminCharges05, edli05: r.edli05, edliCharges001: r.edliCharges001 }))}
            headers={['employeeId','employeeName','payMonth','uan','esi','fatherHusbandName','edliWages','basic','hra','transportAllowance','otherAllowance','telephoneAllowance','totalFinal','employeeEpf12','employerPf367','employerPension833','adminCharges05','edli05','edliCharges001']}
            filename={`hr-pf-${from||'start'}_${to||'end'}`}
            hideImport
          />
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 uppercase"><tr><th className="px-2 py-1.5 text-left">Emp</th><th className="px-2 py-1.5 text-left">Month</th><th className="px-2 py-1.5 text-left">UAN</th><th className="text-right px-2 py-1.5">Basic</th><th className="text-right px-2 py-1.5">Total</th><th className="text-right px-2 py-1.5">EE EPF 12%</th><th className="text-right px-2 py-1.5">ER PF 3.67%</th><th className="text-right px-2 py-1.5">Pension 8.33%</th><th className="text-right px-2 py-1.5">Admin 0.5%</th><th className="text-right px-2 py-1.5">EDLI 0.5%</th></tr></thead>
          <tbody>
            {rows.map((r:any) => <tr key={r.id} className="border-t">
              <td className="px-2 py-1"><div className="text-xs font-medium">{r.employee?.employeeName}</div><div className="text-[10px] text-slate-500 font-mono">{r.employee?.employeeId}</div></td>
              <td className="px-2 py-1 text-xs">{r.payMonth ? new Date(r.payMonth).toLocaleDateString('en-IN',{month:'short',year:'numeric'}) : ''}</td>
              <td className="px-2 py-1 font-mono">{r.uan}</td>
              <td className="px-2 py-1 text-right font-mono">{Number(r.basic).toLocaleString('en-IN')}</td>
              <td className="px-2 py-1 text-right font-mono">{Number(r.totalFinal).toLocaleString('en-IN')}</td>
              <td className="px-2 py-1 text-right font-mono">{Number(r.employeeEpf12).toLocaleString('en-IN')}</td>
              <td className="px-2 py-1 text-right font-mono">{Number(r.employerPf367).toLocaleString('en-IN')}</td>
              <td className="px-2 py-1 text-right font-mono">{Number(r.employerPension833).toLocaleString('en-IN')}</td>
              <td className="px-2 py-1 text-right font-mono">{Number(r.adminCharges05).toLocaleString('en-IN')}</td>
              <td className="px-2 py-1 text-right font-mono">{Number(r.edli05).toLocaleString('en-IN')}</td>
            </tr>)}
            {rows.length===0 && <tr><td colSpan={10} className="text-center py-8 text-slate-400">No PF rows for the selected month range — pick a month above and click “Generate from salary”.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
    </div>
  );
}

function EsiTab() {
  const thisMonth = new Date().toISOString().slice(0,7);
  const [from, setFrom] = useState(thisMonth);
  const [to, setTo] = useState(thisMonth);
  const { data } = useQuery({ queryKey: ['hr','esi'], queryFn: async () => (await api.get('/hr/esi')).data as any[] });
  const filtered = (data ?? []).filter((r: any) => inMonthRange(r.payMonth, from, to));
  const { slice: rows, page, setPage, pageSize, setPageSize, total } = usePaged(filtered, [from, to]);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <MonthRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} label="Show" />
        <div className="text-sm text-slate-500">{total} row{total === 1 ? '' : 's'}</div>
        <div className="ml-auto">
          <ImportExportBar
            rows={filtered.map((r: any) => ({ payMonth: r.payMonth?.slice(0,10), ipNumber: r.ipNumber, ipName: r.ipName, daysPaid: r.daysPaid, monthlyWages: r.monthlyWages, zeroReason: r.zeroReason, lastWorkingDay: r.lastWorkingDay?.slice(0,10) }))}
            headers={['payMonth','ipNumber','ipName','daysPaid','monthlyWages','zeroReason','lastWorkingDay']}
            filename={`hr-esi-${from||'start'}_${to||'end'}`}
            hideImport
          />
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase"><tr><th className="text-left px-3 py-2">Month</th><th className="text-left px-3 py-2">IP Number</th><th className="text-left px-3 py-2">IP Name</th><th className="text-right px-3 py-2">Days Paid</th><th className="text-right px-3 py-2">Monthly Wages</th><th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">Last WD</th></tr></thead>
          <tbody>
            {rows.map((r:any) => <tr key={r.id} className="border-t">
              <td className="px-3 py-1.5 text-xs">{r.payMonth ? new Date(r.payMonth).toLocaleDateString('en-IN',{month:'short',year:'numeric'}) : ''}</td>
              <td className="px-3 py-1.5 font-mono">{r.ipNumber}</td>
              <td className="px-3 py-1.5">{r.ipName}</td>
              <td className="px-3 py-1.5 text-right font-mono">{r.daysPaid}</td>
              <td className="px-3 py-1.5 text-right font-mono">{Number(r.monthlyWages).toLocaleString('en-IN')}</td>
              <td className="px-3 py-1.5">{r.zeroReason}</td>
              <td className="px-3 py-1.5 text-xs">{r.lastWorkingDay ? new Date(r.lastWorkingDay).toLocaleDateString() : ''}</td>
            </tr>)}
            {rows.length===0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No ESI rows for the selected month range</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
    </div>
  );
}
